import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import axios from '../../axios/axiosconf';
import FieldFactory from './FieldFactory';
import { generarPdfConTextoNativo } from '../../../utils/pdfHelpers';
import { evaluateFormula } from '../../../utils/formulaEvaluator';
import Header from '../general/Header';
import Aside from '../general/Aside';
import Footer from '../general/Footer';
import ScrollToEndsButton from '../general/ScrollToEndsButton';
import Loading from '../general/Loading';
import Unauthorized from '../general/Unauthorized';
import RegistroRipsSinFev from '../rips/RegistroRipsSinFev';
import RegistroRipsFev from '../rips/RegistroRipsFev';
import RegistroRipsConvenio from '../rips/RegistroRipsConvenio';
import ModalExamenes from './ModalExamenes';
import ModalFormulas from './ModalFormulas';
import ModalIncapacidades from './ModalIncapacidades';
import ModalRecomendaciones from './ModalRecomendaciones';
import ModalRemisiones from './ModalRemisiones';
import ModalContraremisiones from './ModalContraremisiones';
import ModalConsentimientos from './ModalConsentimientos';
import ModalOdontograma from './ModalOdontograma';
import ModalArchivosClinicos from './ModalArchivosClinicos';
import DocumentoPacienteModal from './DocumentoPacienteModal';
import ModalNotaAclaratoria from './ModalNotaAclaratoria';
import '../common/ModalEnviarEmail.css';

/**
 * IDs de `plantilla_admin_id` en producción (plantillas padre en admin; copias por cliente en
 * `plantillas_historia_clinica`). Todas las historias JSON usan el mismo esquema de registro;
 * no hay columnas distintas por tipo: solo cambia el id de plantilla y el JSON en `datos`.
 * - 26: Medicina Laboral (origen datos empresa/cargo para certificado)
 * - 27, 28: Certificados (p. ej. CMO / recomendaciones) — flujo “Generar certificados”
 */
const PLANTILLA_ADMIN_PROD = {
    MEDICINA_LABORAL: 26,
    CERTIFICADO_27: 27,
    CERTIFICADO_28: 28
};

const PLANTILLAS_CERTIFICADO_GENERABLE = new Set([
    PLANTILLA_ADMIN_PROD.CERTIFICADO_27,
    PLANTILLA_ADMIN_PROD.CERTIFICADO_28
]);

/** estado en historia_clinica_registros: 0=guardado, 1=borrador */
const ESTADO_HISTORIA_GUARDADA = 0;
const ESTADO_HISTORIA_BORRADOR = 1;

const escapeHtmlNota = (texto) =>
    String(texto ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/\n/g, '<br />');

const PLANTILLA_ADMIN_MEDICINA_LABORAL = PLANTILLA_ADMIN_PROD.MEDICINA_LABORAL;

const LABOR_FIELD_DEFS = [
    ['empresa', 'EMPRESA'],
    ['nit_empresa', 'NIT'],
    ['sede_trabajo', 'SEDE O CENTRO DE TRABAJO'],
    ['area_trabajo', 'ÁREA O DEPARTAMENTO'],
    ['cargo_actual', 'CARGO ACTUAL'],
    ['fecha_ingreso_empresa', 'FECHA DE INGRESO A LA EMPRESA'],
    ['tipo_contrato', 'TIPO DE CONTRATO']
];

const parseDatosHistoriaRegistro = (registro) => {
    if (!registro || registro.datos == null) return {};
    try {
        return typeof registro.datos === 'string' ? JSON.parse(registro.datos) : registro.datos;
    } catch {
        return {};
    }
};

const formatValorLaborPdf = (campoId, valor) => {
    if (valor === undefined || valor === null || valor === '') return '';
    if (
        campoId === 'fecha_ingreso_empresa' &&
        typeof valor === 'string' &&
        /^\d{4}-\d{2}-\d{2}/.test(valor.trim())
    ) {
        const [y, m, d] = valor.trim().slice(0, 10).split('-');
        return `${d}/${m}/${y}`;
    }
    return String(valor);
};

const filasLaboralesParaPdf = (datos) => {
    const filas = [];
    if (!datos || typeof datos !== 'object') return filas;
    LABOR_FIELD_DEFS.forEach(([id, label]) => {
        const v = formatValorLaborPdf(id, datos[id]);
        if (v !== '') filas.push({ etiqueta: label, valor: v });
    });
    return filas;
};

const esHistoriaMedicinaLaboral = (registro) => {
    if (!registro) return false;
    if (Number(registro.plantilla_admin_id) === PLANTILLA_ADMIN_MEDICINA_LABORAL) return true;
    const nombre = String(registro.plantilla_nombre || '').toLowerCase();
    if (nombre.includes('medicina laboral')) return true;
    return false;
};

/**
 * HistoriaClinicaJsonPage - Página completa para llenar historia clínica usando plantillas JSON
 * 
 * Props: Ninguno (lee pacienteId desde URL query params)
 */
const HistoriaClinicaJsonPage = () => {
    const history = useHistory();
    const queryParams = useLocation().search;
    const paciente_id = new URLSearchParams(queryParams).get('id');
    
    const [loggedIn, setLoggedIn] = useState(true);
    const [hasPermission, setHasPermission] = useState(null); // 🔒 Control de acceso por permiso
    const [loadingPaciente, setLoadingPaciente] = useState(true);
    const [errorPaciente, setErrorPaciente] = useState(null);
    const [paciente, setPaciente] = useState(null);
    const [pacienteId, setPacienteId] = useState(paciente_id);
    const [plantillas, setPlantillas] = useState([]);
    const [plantillaSeleccionada, setPlantillaSeleccionada] = useState(null);
    const [plantillaId, setPlantillaId] = useState('');
    const [valores, setValores] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingPlantillas, setLoadingPlantillas] = useState(false);
    
    // 🆕 Estados adicionales siguiendo patrón de ModalEvolucion
    const [fechaHora, setFechaHora] = useState('');
    const [nombreProfesional, setNombreProfesional] = useState('');
    const [registroMedico, setRegistroMedico] = useState('');
    const [codigoHabilitacion, setCodigoHabilitacion] = useState('');
    const [firmaimg, setFirmaimg] = useState('');
    const [textoPieFirma, setTextoPieFirma] = useState('');
    
    // 🆕 Estados para historial y modales
    const [historial, setHistorial] = useState([]);
    const [vistaPiRevision, setVistaPiRevision] = useState(0);
    const [showModalVistaPrevia, setShowModalVistaPrevia] = useState(false);
    const [historiaSeleccionada, setHistoriaSeleccionada] = useState(null);
    const [showModalConfirmacion, setShowModalConfirmacion] = useState(false);
    const [showModalConsolidado, setShowModalConsolidado] = useState(false);
    const [consolidado, setConsolidado] = useState('');
    const [showModalEmailHistoria, setShowModalEmailHistoria] = useState(false);
    const [emailDestinoHistoria, setEmailDestinoHistoria] = useState('');
    const [enviandoEmailHistoria, setEnviandoEmailHistoria] = useState(false);
    const [tipoEmailHistoria, setTipoEmailHistoria] = useState('consolidado');
    const [mensajeConfirmacion, setMensajeConfirmacion] = useState('');
    const [registroEdicionId, setRegistroEdicionId] = useState(null);
    const [usuarioActualId, setUsuarioActualId] = useState(null);
    const [notasAclaratorias, setNotasAclaratorias] = useState([]);
    const [showModalNotaAclaratoria, setShowModalNotaAclaratoria] = useState(false);
    const [encabezadoPersonalizado, setEncabezadoPersonalizado] = useState(null);
    const [showModalElegirMedicinaLaboral, setShowModalElegirMedicinaLaboral] = useState(false);
    const [registroMedicinaLaboralElegido, setRegistroMedicinaLaboralElegido] = useState(null);
    const [showModalVistaCertificadoLabor, setShowModalVistaCertificadoLabor] = useState(false);

    // Estados para modales de documentos (menú lateral)
    const [showRipsFev, setShowRipsFev] = useState(false);
    const [showRipsSinFev, setShowRipsSinFev] = useState(false);
    const [showRipsConvenio, setShowRipsConvenio] = useState(false);
    const [showExamenes, setShowExamenes] = useState(false);
    const [showFormulas, setShowFormulas] = useState(false);
    const [showIncapacidades, setShowIncapacidades] = useState(false);
    const [showRecomendaciones, setShowRecomendaciones] = useState(false);
    const [showRemisiones, setShowRemisiones] = useState(false);
    const [showContraremisiones, setShowContraremisiones] = useState(false);
    const [showConsentimientos, setShowConsentimientos] = useState(false);
    const [showOdontograma, setShowOdontograma] = useState(false);
    const [showArchivosClinicos, setShowArchivosClinicos] = useState(false);
    const [esFacturador, setEsFacturador] = useState(false);
    const [tienePermisoDocumentos, setTienePermisoDocumentos] = useState(false);
    const [tienePermisoRips, setTienePermisoRips] = useState(false);
    const [tienePermisoToxina, setTienePermisoToxina] = useState(false);
    const [tienePermisoOdontograma, setTienePermisoOdontograma] = useState(false);
    const [tienePermisoEvolucion, setTienePermisoEvolucion] = useState(false);
    const [tienePermisoHistoriaClinica, setTienePermisoHistoriaClinica] = useState(false);

    // Estados para cargar historial en modales
    const [cargarHistorialExamenes, setCargarHistorialExamenes] = useState(false);
    const [cargarHistorialFormulas, setCargarHistorialFormulas] = useState(false);
    const [cargarHistorialIncapacidades, setCargarHistorialIncapacidades] = useState(false);
    const [cargarHistorialRecomendaciones, setCargarHistorialRecomendaciones] = useState(false);
    const [cargarHistorialRemisiones, setCargarHistorialRemisiones] = useState(false);
    const [cargarHistorialContraremisiones, setCargarHistorialContraremisiones] = useState(false);
    const [cargarHistorialConsentimientos, setCargarHistorialConsentimientos] = useState(false);
    const [cargarHistorialRipsConvenio, setCargarHistorialRipsConvenio] = useState(false);

    const esHistoriaBorrador = (h) => Number(h?.estado ?? ESTADO_HISTORIA_GUARDADA) === ESTADO_HISTORIA_BORRADOR;
    const esHistoriaGuardada = (h) => Number(h?.estado ?? ESTADO_HISTORIA_GUARDADA) === ESTADO_HISTORIA_GUARDADA;

    const historialesMedicinaLaboral = useMemo(
        () => (Array.isArray(historial)
            ? historial.filter((h) => esHistoriaMedicinaLaboral(h) && esHistoriaGuardada(h))
            : []),
        [historial]
    );

    const historialSoloGuardadas = useMemo(
        () => (Array.isArray(historial) ? historial.filter(esHistoriaGuardada) : []),
        [historial]
    );

    const puedeEditarBorrador = (item) =>
        esHistoriaBorrador(item)
        && usuarioActualId != null
        && Number(item.user_id) === Number(usuarioActualId);

    const puedeAgregarNotaAclaratoria = Boolean(
        historiaSeleccionada
        && esHistoriaGuardada(historiaSeleccionada)
        && usuarioActualId != null
        && Number(historiaSeleccionada.user_id) === Number(usuarioActualId)
    );

    const notasDeRegistro = (registroId) =>
        (notasAclaratorias || []).filter((n) => Number(n.id_evolucion_historia) === Number(registroId));

    const formatearFechaNotaDisplay = (fecha) => {
        if (!fecha) return '';
        const s = String(fecha).replace('T', ' ');
        return s.length > 16 ? s.slice(0, 16) : s;
    };

    const htmlNotasAclaratoriasRegistro = (registroId) => {
        const notas = notasDeRegistro(registroId);
        if (!notas.length) return '';
        return notas.map((n) => `
            <div style="margin: 12px 0 8px; padding: 10px 12px; border-left: 3px solid #17a2b8; background: #f8f9fa;">
                <div style="font-weight: 700; letter-spacing: 0.03em; margin-bottom: 4px;">NOTA ACLARATORIA</div>
                <div style="font-size: 11px; color: #6c757d; margin-bottom: 6px;">Fecha: ${escapeHtmlNota(formatearFechaNotaDisplay(n.fecha))}</div>
                <div style="white-space: normal;">${escapeHtmlNota(n.nota_aclaratoria)}</div>
            </div>
        `).join('');
    };

    const construirSeccionesNotasAclaratoriasPdf = (registroId) => {
        const notas = notasDeRegistro(registroId);
        if (!notas.length) return [];
        return notas.map((n, idx) => ({
            tipo: 'texto',
            titulo: notas.length > 1 ? `Nota aclaratoria (${idx + 1})` : 'Nota aclaratoria',
            contenido: `<p><strong>Fecha:</strong> ${escapeHtmlNota(formatearFechaNotaDisplay(n.fecha))}</p><p>${escapeHtmlNota(n.nota_aclaratoria)}</p>`,
            estilos: { margenSuperiorMm: 3, htmlTextoCompacto: false },
        }));
    };

    const registroTieneNotas = (registroId) => notasDeRegistro(registroId).length > 0;

    const cargarNotasAclaratorias = async () => {
        if (!pacienteId) {
            setNotasAclaratorias([]);
            return [];
        }
        try {
            const response = await axios.get('/notas-aclaratorias', {
                params: { tipo: 'historia', paciente_id: pacienteId },
            });
            const notas = response.data?.notas || [];
            setNotasAclaratorias(notas);
            return notas;
        } catch (error) {
            console.log('No se pudieron cargar notas aclaratorias:', error);
            setNotasAclaratorias([]);
            return [];
        }
    };

    const notasRegistroActual = useMemo(
        () => (historiaSeleccionada ? notasDeRegistro(historiaSeleccionada.id) : []),
        [historiaSeleccionada?.id, notasAclaratorias]
    );
    const tieneNotasEnRegistro = notasRegistroActual.length > 0;
    const puedeAbrirModalNota = Boolean(
        historiaSeleccionada && esHistoriaGuardada(historiaSeleccionada)
    );
    const botonNotaDisabled = !puedeAbrirModalNota
        || (!tieneNotasEnRegistro && !puedeAgregarNotaAclaratoria);

    const edicionBorradorActiva = Boolean(
        registroEdicionId && historiaSeleccionada && esHistoriaBorrador(historiaSeleccionada)
    );
    
    // 🔒 VALIDACIÓN DE PERMISOS - Verificar acceso a historia clínica
    useEffect(() => {
        if (localStorage.getItem('userdata')) {
            const userdata = JSON.parse(localStorage.getItem('userdata'));
            
            let tienePermiso = false;
            if (userdata.permissions && Array.isArray(userdata.permissions)) {
                tienePermiso = userdata.permissions.some(p => p.slug === 'historiaClinicaPaciente');
            } else if (userdata.up && userdata.up.includes('historiaClinicaPaciente')) {
                tienePermiso = true;
            }
            
            setHasPermission(tienePermiso);
            setUsuarioActualId(userdata.user_id ?? null);

            // Verificar permisos del menú lateral
            const verificarPermisoSlug = (slug) => {
                if (userdata.permissions && Array.isArray(userdata.permissions)) {
                    return userdata.permissions.some(p => p.slug === slug);
                }
                if (userdata.up && Array.isArray(userdata.up)) {
                    return userdata.up.includes(slug);
                }
                return false;
            };
            setTienePermisoDocumentos(verificarPermisoSlug('documentosPaciente'));
            setTienePermisoRips(verificarPermisoSlug('ripsPaciente'));
            setTienePermisoToxina(verificarPermisoSlug('toxinaPaciente'));
            setTienePermisoOdontograma(verificarPermisoSlug('odontogramaPaciente'));
            setTienePermisoEvolucion(verificarPermisoSlug('evolucionPaciente'));
            setTienePermisoHistoriaClinica(verificarPermisoSlug('historiaClinicaPaciente'));
            setEsFacturador(parseInt(userdata?.facturador, 10) === 0);
        } else {
            setHasPermission(false);
        }
    }, []);
    
    // 🆕 Cargar datos del paciente desde URL
    useEffect(() => {
        if (!hasPermission) return; // No cargar si no tiene permiso
        
        if (paciente_id) {
            setLoadingPaciente(true);
            setErrorPaciente(null);
            
            // Usar el mismo endpoint que ModalEvolucion.js con query params
            const userdata = JSON.parse(localStorage.getItem('userdata') || '{}');
            const configPaciente = {
                headers: {
                    Authorization: 'Bearer ' + userdata.access_token
                },
                params: {
                    id: paciente_id
                }
            };
            
            console.log('🔍 Cargando datos del paciente con ID:', paciente_id);
            
            axios.get('/paciente', configPaciente)
                .then(response => {
                    if (response.status === 200) {
                        console.log('✅ Datos del paciente recibidos del backend:', response.data);
                        
                        // El endpoint /paciente devuelve un array con un objeto
                        const datosPaciente = Array.isArray(response.data) ? response.data[0] : response.data;
                        
                        console.log('📋 Datos del paciente procesados:', {
                            primerNombre: datosPaciente.primer_nombre,
                            segundoNombre: datosPaciente.segundo_nombre,
                            primerApellido: datosPaciente.primer_apellido,
                            segundoApellido: datosPaciente.segundo_apellido,
                            tipoDocumento: datosPaciente.tipo_documento,
                            numeroDocumento: datosPaciente.numero_documento
                        });
                        
                        // Normalizar nombres de campos (snake_case a camelCase)
                        const pacienteNormalizado = {
                            primerNombre: datosPaciente.primer_nombre,
                            segundoNombre: datosPaciente.segundo_nombre,
                            primerApellido: datosPaciente.primer_apellido,
                            segundoApellido: datosPaciente.segundo_apellido,
                            tipoDocumento: datosPaciente.tipo_documento,
                            numeroDocumento: datosPaciente.numero_documento,
                            ...datosPaciente
                        };
                        
                        setPaciente(pacienteNormalizado);
                        console.log('✅ Estado paciente actualizado:', pacienteNormalizado);
                    }
                    setLoadingPaciente(false);
                })
                .catch(error => {
                    console.error('❌ Error cargando datos del paciente:', error);
                    console.error('❌ Detalles del error:', error.response?.data);
                    setErrorPaciente('No se pudieron cargar los datos del paciente');
                    setLoadingPaciente(false);
                });
        } else {
            console.error('❌ No se proporcionó ID de paciente en la URL');
            setErrorPaciente('No se proporcionó ID de paciente en la URL');
            setLoadingPaciente(false);
        }
    }, [paciente_id, hasPermission]);
    
    // 🆕 HELPER: Obtener datos del paciente (nombre completo y documento)
    const obtenerDatosPaciente = () => {
        console.log('🔍 obtenerDatosPaciente - paciente:', paciente);
        
        if (!paciente) {
            console.warn('⚠️ No hay datos del paciente disponibles');
            return {
                nombreCompleto: 'Paciente no identificado',
                documentoCompleto: 'Documento no disponible'
            };
        }
        
        // Construir nombre completo
        const primerNombre = paciente.primerNombre || paciente.primer_nombre || '';
        const segundoNombre = paciente.segundoNombre || paciente.segundo_nombre || '';
        const primerApellido = paciente.primerApellido || paciente.primer_apellido || '';
        const segundoApellido = paciente.segundoApellido || paciente.segundo_apellido || '';
        const tipoDocumento = paciente.tipoDocumento || paciente.tipo_documento || '';
        const numeroDocumento = paciente.numeroDocumento || paciente.numero_documento || '';
        
        const nombreCompleto = `${primerNombre} ${segundoNombre} ${primerApellido} ${segundoApellido}`.trim() || 'Paciente no identificado';
        
        // Construir documento completo solo si hay datos válidos
        let documentoCompleto = 'Documento no disponible';
        if (tipoDocumento && numeroDocumento) {
            documentoCompleto = `${tipoDocumento} - ${numeroDocumento}`;
        } else if (numeroDocumento) {
            documentoCompleto = numeroDocumento;
        }
        
        console.log('✅ obtenerDatosPaciente - resultado:', { nombreCompleto, documentoCompleto });
        
        return {
            nombreCompleto,
            documentoCompleto,
            ...paciente
        };
    };
    
    const valorNoAplica = (v) => {
        if (v === null || v === undefined) return 'No registra';
        const s = String(v).trim();
        return s ? s : 'No registra';
    };

    const formatearFechaNacimientoCert = (raw) => {
        if (!raw) return 'No registra';
        const s = String(raw).trim();
        let d = null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
            d = new Date(`${s}T00:00:00`);
        } else {
            d = new Date(s);
        }
        if (Number.isNaN(d.getTime())) return 'No registra';
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const anio = d.getFullYear();
        return `${dia}/${mes}/${anio}`;
    };

    const calcularEdadDesdeFechaNacimiento = (raw) => {
        if (!raw) return 'No registra';
        const s = String(raw).trim();
        let nacimiento = null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
            nacimiento = new Date(`${s}T00:00:00`);
        } else {
            nacimiento = new Date(s);
        }
        if (Number.isNaN(nacimiento.getTime())) return 'No registra';

        const hoy = new Date();
        let edad = hoy.getFullYear() - nacimiento.getFullYear();
        const m = hoy.getMonth() - nacimiento.getMonth();
        if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) {
            edad--;
        }
        return edad >= 0 ? `${edad} años` : 'No registra';
    };

    const obtenerSexoPacienteTexto = (rawSexo) => {
        const sexo = String(rawSexo || '').trim();
        if (sexo === '01') return 'Masculino';
        if (sexo === '02') return 'Femenino';
        return 'No registra';
    };

    const obtenerFilasPacienteCertificado = () => {
        const datos = obtenerDatosPaciente();
        return [
            { etiqueta: 'SEXO', valor: obtenerSexoPacienteTexto(datos.sexo) },
            { etiqueta: 'FECHA DE NACIMIENTO', valor: formatearFechaNacimientoCert(datos.fecha_nacimiento || datos.fechaNacimiento) },
            { etiqueta: 'EDAD', valor: calcularEdadDesdeFechaNacimiento(datos.fecha_nacimiento || datos.fechaNacimiento) },
            { etiqueta: 'EMAIL', valor: valorNoAplica(datos.email) },
            { etiqueta: 'CIUDAD', valor: valorNoAplica(datos.nombre_ciudad_residencia || datos.nombreCiudadResidencia || datos.ciudad) },
            { etiqueta: 'DIRECCIÓN', valor: valorNoAplica(datos.direccion) },
            { etiqueta: 'EPS', valor: valorNoAplica(datos.nombre_eps || datos.nombreEps) }
        ];
    };

    const escapeHtmlBasicoHc = (s) => String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const fechaHistoriaParaInput = (raw) => {
        if (raw == null || raw === '') return '';
        const s = String(raw).trim();
        if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) {
            return s.replace(' ', 'T').slice(0, 16);
        }
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) {
            return s.slice(0, 16);
        }
        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return s.length >= 16 ? s.slice(0, 16) : s;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const h = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${y}-${m}-${day}T${h}:${min}`;
    };

    const fechaHistoriaParaDisplay = (raw) => {
        const inputFmt = fechaHistoriaParaInput(raw);
        if (!inputFmt) return '';
        const d = new Date(inputFmt);
        if (Number.isNaN(d.getTime())) return String(raw || '').trim();
        try {
            return d.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
        } catch (e) {
            return inputFmt.replace('T', ' ');
        }
    };

    /** Fecha corta tipo 27/04/26 para barra del certificado (sin hora). */
    const fechaHistoriaSoloFechaCorta = (raw) => {
        const inputFmt = fechaHistoriaParaInput(raw);
        if (!inputFmt) return '';
        const d = new Date(inputFmt);
        if (Number.isNaN(d.getTime())) return '';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(-2);
        return `${dd}/${mm}/${yy}`;
    };

    const pieFirmaHistoriaProfesional = (historia) => {
        const desdeApi = historia && historia.texto_pie_firma_usuario != null && historia.texto_pie_firma_usuario !== ''
            ? String(historia.texto_pie_firma_usuario).trim()
            : '';
        if (desdeApi) return desdeApi;
        return (textoPieFirma && String(textoPieFirma).trim()) || '';
    };
    
    // Cargar plantillas y datos profesional al montar; historial vía vistaPiRevision
    useEffect(() => {
        if (paciente_id) {
            cargarPlantillas();
            cargarDatosProfesional();
        }
    }, [paciente_id]);

    useEffect(() => {
        const onVistaPiChanged = () => setVistaPiRevision((v) => v + 1);
        window.addEventListener('smile-vista-pi-changed', onVistaPiChanged);
        return () => window.removeEventListener('smile-vista-pi-changed', onVistaPiChanged);
    }, []);

    useEffect(() => {
        if (!paciente_id) return;
        cargarHistorial();
    }, [paciente_id, vistaPiRevision]);
    
    // 🆕 Cargar historial de historias clínicas
    const cargarHistorial = () => {
        if (!pacienteId) {
            console.log('⚠️ No hay pacienteId, no se carga historial');
            return;
        }
        
        console.log('📥 Cargando historial para paciente:', pacienteId);
        
        axios.get('/historia-clinica-json', {
            params: { paciente_id: pacienteId }
        })
            .then(response => {
                console.log('✅ Historial cargado exitosamente');
                console.log('📋 Cantidad de registros:', response.data.length);
                console.log('📄 Datos completos:', response.data);
                
                // Log detallado del primer registro si existe
                if (response.data.length > 0) {
                    const primerRegistro = response.data[0];
                    console.log('🔍 Primer registro detallado:');
                    console.log('  - ID:', primerRegistro.id);
                    console.log('  - Fecha:', primerRegistro.fecha);
                    console.log('  - Plantilla:', primerRegistro.plantilla_nombre);
                    console.log('  - Datos (tipo):', typeof primerRegistro.datos);
                    console.log('  - Datos (valor):', primerRegistro.datos);
                    console.log('  - Estructura (tipo):', typeof primerRegistro.plantilla_estructura);
                    console.log('  - Estructura (valor):', primerRegistro.plantilla_estructura);
                }
                
                setHistorial(response.data);
                cargarNotasAclaratorias();
            })
            .catch(error => {
                console.error('❌ Error cargando historial:', error);
                console.error('📋 Detalles del error:', {
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                    message: error.message
                });
                setHistorial([]);
                setNotasAclaratorias([]);
            });
    };
    
    // 🆕 Cargar datos del profesional (siguiendo patrón de ModalEvolucion)
    const cargarDatosProfesional = () => {
        axios.get('/perfil')
            .then(response => {
                if (response.status === 200) {
                    setNombreProfesional(response.data.name + ' ' + response.data.lastname);
                    setRegistroMedico(response.data.registro_medico ?? '');
                    setCodigoHabilitacion(response.data.codigo_habilitacion ?? '');
                    setFirmaimg(response.data.firma_blob || '');
                    setTextoPieFirma(response.data.texto_pie_firma || '');
                }
            })
            .catch(error => {
                console.error('Error cargando datos del profesional:', error);
            });
        
        // 🆕 Cargar encabezado personalizado
        const userdata = JSON.parse(localStorage.getItem('userdata'));
        axios.get('/encabezado-personalizado?uid=' + userdata.user_id)
            .then(response => {
                if (response.status === 200 && response.data && response.data.estado === 1) {
                    setEncabezadoPersonalizado(response.data);
                }
            })
            .catch(error => {
                console.log('No hay encabezado personalizado:', error);
                setEncabezadoPersonalizado(null);
            });
    };
    
    // 🆕 Establecer fecha y hora actual
    const establecerFechaHoraActual = () => {
        const ahora = new Date();
        const y = ahora.getFullYear();
        const m = String(ahora.getMonth() + 1).padStart(2, '0');
        const day = String(ahora.getDate()).padStart(2, '0');
        const h = String(ahora.getHours()).padStart(2, '0');
        const min = String(ahora.getMinutes()).padStart(2, '0');
        setFechaHora(`${y}-${m}-${day}T${h}:${min}`);
    };

    // 🆕 Funciones para modales de documentos (menú lateral)
    const abrirExamenes = () => { setShowExamenes(true); setCargarHistorialExamenes(true); };
    const cerrarExamenes = () => { setShowExamenes(false); setCargarHistorialExamenes(false); };

    const abrirFormulas = () => { setShowFormulas(true); setCargarHistorialFormulas(true); };
    const cerrarFormulas = () => { setShowFormulas(false); setCargarHistorialFormulas(false); };

    const abrirIncapacidades = () => { setShowIncapacidades(true); setCargarHistorialIncapacidades(true); };
    const cerrarIncapacidades = () => { setShowIncapacidades(false); setCargarHistorialIncapacidades(false); };

    const abrirRecomendaciones = () => { setShowRecomendaciones(true); setCargarHistorialRecomendaciones(true); };
    const cerrarRecomendaciones = () => { setShowRecomendaciones(false); setCargarHistorialRecomendaciones(false); };

    const abrirRemisiones = () => { setShowRemisiones(true); setCargarHistorialRemisiones(true); };
    const cerrarRemisiones = () => { setShowRemisiones(false); setCargarHistorialRemisiones(false); };

    const abrirContraremisiones = () => { setShowContraremisiones(true); setCargarHistorialContraremisiones(true); };
    const cerrarContraremisiones = () => { setShowContraremisiones(false); setCargarHistorialContraremisiones(false); };

    const abrirConsentimientos = () => { setShowConsentimientos(true); setCargarHistorialConsentimientos(true); };
    const cerrarConsentimientos = () => { setShowConsentimientos(false); setCargarHistorialConsentimientos(false); };

    const abrirOdontograma = () => { setShowOdontograma(true); };
    const cerrarOdontograma = () => { setShowOdontograma(false); };

    const abrirArchivosClinicos = () => {
        setShowArchivosClinicos(true);
    };
    const cerrarArchivosClinicos = () => {
        setShowArchivosClinicos(false);
    };

    const abrirToxina = () => {
        history.push(`/toxina-botulinica?id=${paciente_id}`);
    };

    const abrirRipsConvenio = () => { setShowRipsConvenio(true); setCargarHistorialRipsConvenio(true); };
    const cerrarRipsConvenio = () => { setShowRipsConvenio(false); setCargarHistorialRipsConvenio(false); };

    const abrirEvolucion = () => {
        history.push(`/evolucion-paciente?id=${paciente_id}`);
    };

    // Función para obtener datos de anamnesis (placeholder)
    const getInfoAnamnesisPDF = () => '';

    // Función para obtener datos del paciente para RIPS
    const obtenerPacienteParaRips = () => ({
        id: paciente?.id || paciente_id,
        primerNombre: paciente?.primerNombre || paciente?.primer_nombre || '',
        segundoNombre: paciente?.segundoNombre || paciente?.segundo_nombre || '',
        primerApellido: paciente?.primerApellido || paciente?.primer_apellido || '',
        segundoApellido: paciente?.segundoApellido || paciente?.segundo_apellido || '',
        numeroDocumento: paciente?.numeroDocumento || paciente?.numero_documento || '',
        tipoDocumento: paciente?.tipoDocumento || paciente?.tipo_documento || 'CC',
    });
    
    const cargarPlantillas = () => {
        setLoadingPlantillas(true);
        
        axios.get('/mis-plantillas')
            .then(response => {
                setPlantillas(response.data);
                setLoadingPlantillas(false);
            })
            .catch(error => {
                console.error('Error cargando plantillas:', error);
                setLoadingPlantillas(false);
                alert('Error al cargar plantillas. Por favor intente nuevamente.');
            });
    };

    /** El cliente dispone de al menos una plantilla CMO/CMR (27/28) en su catálogo */
    const tienePlantillasCertificadoEnCliente = useMemo(
        () =>
            (Array.isArray(plantillas) ? plantillas : []).some((p) =>
                PLANTILLAS_CERTIFICADO_GENERABLE.has(Number(p?.plantilla_admin_id))
            ),
        [plantillas]
    );

    const plantillaDesplegableEsCertificado = useMemo(() => {
        if (!plantillaId) return false;
        const p = plantillas.find((x) => String(x.id) === String(plantillaId));
        return !!(p && PLANTILLAS_CERTIFICADO_GENERABLE.has(Number(p.plantilla_admin_id)));
    }, [plantillaId, plantillas]);

    const puedeGenerarCertificado = useMemo(() => {
        if (!tienePlantillasCertificadoEnCliente) return false;
        const historiaOk =
            historiaSeleccionada &&
            PLANTILLAS_CERTIFICADO_GENERABLE.has(Number(historiaSeleccionada.plantilla_admin_id));
        return historiaOk || plantillaDesplegableEsCertificado;
    }, [tienePlantillasCertificadoEnCliente, historiaSeleccionada, plantillaDesplegableEsCertificado]);
    
    // Manejar cambio de plantilla
    const handlePlantillaChange = (e) => {
        const selectedId = e.target.value;
        setPlantillaId(selectedId);
        
        if (!selectedId) {
            setPlantillaSeleccionada(null);
            setValores({});
            return;
        }
        
        const plantilla = plantillas.find(p => p.id == selectedId);
        
        if (plantilla) {
            try {
                const estructura = typeof plantilla.estructura_json === 'string' 
                    ? JSON.parse(plantilla.estructura_json) 
                    : plantilla.estructura_json;
                
                setPlantillaSeleccionada(estructura);
                setValores({});
            } catch (error) {
                console.error('Error parseando estructura JSON:', error);
                alert('Error al cargar la estructura de la plantilla.');
            }
        }
    };
    
    // Manejar cambio de valor de campo
    const handleFieldChange = (campoId, valor) => {
        const nuevosValores = { ...valores, [campoId]: valor };
        
        // Recalcular todos los campos fórmula
        if (plantillaSeleccionada && plantillaSeleccionada.secciones) {
            plantillaSeleccionada.secciones.forEach(seccion => {
                if (seccion.campos) {
                    seccion.campos.forEach(campo => {
                        if (campo.tipo === 'formula') {
                            const resultado = evaluateFormula(campo.formula, nuevosValores);
                            if (resultado.value !== null) {
                                nuevosValores[campo.id] = Number(resultado.value.toFixed(campo.decimales ?? 2));
                            } else {
                                nuevosValores[campo.id] = '';
                            }
                        }
                    });
                }
            });
        }
        
        setValores(nuevosValores);
    };
    
    // Guardar historia clínica (estado 0=definitivo, 1=borrador)
    const limpiarFormularioTrasGuardar = () => {
        setPlantillaId('');
        setPlantillaSeleccionada(null);
        setValores({});
        setFechaHora('');
        setHistoriaSeleccionada(null);
        setRegistroEdicionId(null);
    };

    const persistirHistoria = (estadoDestino, e) => {
        if (e) e.preventDefault();
        
        if (!plantillaId) {
            alert('Por favor seleccione una plantilla');
            return;
        }
        
        if (!fechaHora) {
            alert('Por favor establezca la fecha y hora');
            return;
        }
        
        const esEdicionBorrador = Boolean(
            registroEdicionId && historiaSeleccionada && esHistoriaBorrador(historiaSeleccionada)
        );

        if (esEdicionBorrador && !puedeEditarBorrador(historiaSeleccionada)) {
            alert('Este borrador solo puede ser completado por el profesional que lo inició.');
            return;
        }

        if (historiaSeleccionada && esHistoriaGuardada(historiaSeleccionada) && !esEdicionBorrador) {
            alert('Esta historia ya está guardada. Use «Nueva Historia» para crear un registro nuevo.');
            return;
        }

        setLoading(true);

        const onExito = (response, esUpdate) => {
                setLoading(false);
            cargarHistorial();

            if (estadoDestino === ESTADO_HISTORIA_GUARDADA) {
                limpiarFormularioTrasGuardar();
                setMensajeConfirmacion('La historia clínica ha sido guardada exitosamente.');
            } else {
                const newId = esUpdate ? registroEdicionId : response.data?.id;
                if (newId) {
                    setRegistroEdicionId(newId);
                    setHistoriaSeleccionada((prev) => ({
                        ...(prev || {}),
                        id: newId,
                        estado: ESTADO_HISTORIA_BORRADOR,
                        user_id: usuarioActualId,
                        plantilla_id: plantillaId,
                        datos: JSON.stringify(valores),
                        fecha: fechaHora,
                        profesional: nombreProfesional,
                    }));
                }
                setMensajeConfirmacion('Borrador guardado. Puede continuar editando el contenido.');
            }

            setTimeout(() => setShowModalConfirmacion(true), 300);
        };

        const onError = (error) => {
                setLoading(false);
                console.error('❌ Error guardando historia:', error);
            if (error.response?.data?.message) {
                    alert('Error: ' + error.response.data.message);
                } else {
                    alert('Error al guardar historia clínica. Por favor intente nuevamente.');
                }
        };

        if (esEdicionBorrador) {
            axios.put(`/historia-clinica-json/${registroEdicionId}`, {
                datos: JSON.stringify(valores),
                estado: estadoDestino,
            })
                .then((response) => onExito(response, true))
                .catch(onError);
            return;
        }

        const data = {
            paciente_id: pacienteId,
            plantilla_id: plantillaId,
            datos: JSON.stringify(valores),
            fecha: fechaHora,
            profesional: nombreProfesional,
            registro_medico: registroMedico || '',
            codigo_habilitacion: codigoHabilitacion || '',
            estado: estadoDestino,
        };

        axios.post('/historia-clinica-json', data)
            .then((response) => onExito(response, false))
            .catch(onError);
    };

    const handleSubmit = (e) => persistirHistoria(ESTADO_HISTORIA_GUARDADA, e);
    
    const nuevaHistoria = () => {
        setPlantillaId('');
        setPlantillaSeleccionada(null);
        setValores({});
        setFechaHora('');
        setHistoriaSeleccionada(null);
        setRegistroEdicionId(null);
        cargarDatosProfesional();
    };

    const continuarBorrador = (historia, e) => {
        if (e) e.stopPropagation();
        verHistoria(historia);
    };
    
    // Ver historia clínica cargando datos en el formulario
    const verHistoria = (historia) => {
        if (esHistoriaBorrador(historia) && !puedeEditarBorrador(historia)) {
            alert('Este borrador solo puede ser completado por el profesional que lo inició.');
            return;
        }

        console.log('📖 Cargando historia en formulario:', historia);
        
        // 1. Seleccionar la plantilla
        setPlantillaId(historia.plantilla_id);
        
        // 2. Parsear y cargar la estructura
        try {
            const estructura = typeof historia.plantilla_estructura === 'string' 
                ? JSON.parse(historia.plantilla_estructura) 
                : historia.plantilla_estructura;
            setPlantillaSeleccionada(estructura);
            
            // 3. Cargar los valores guardados
            const datos = typeof historia.datos === 'string' 
                ? JSON.parse(historia.datos) 
                : historia.datos;
            setValores(datos);
            
            // 4. Cargar fecha (datetime-local exige formato YYYY-MM-DDTHH:mm)
            setFechaHora(fechaHistoriaParaInput(historia.fecha));

            if (esHistoriaBorrador(historia)) {
                setRegistroEdicionId(historia.id);
                setNombreProfesional(historia.profesional || '');
                setRegistroMedico(historia.registro_medico || '');
                setCodigoHabilitacion(historia.codigo_habilitacion || '');
            } else {
                setRegistroEdicionId(null);
            }
            
            // 5. Marcar como seleccionada
            setHistoriaSeleccionada(historia);
            
            console.log('✅ Historia cargada en formulario exitosamente');
        } catch (error) {
            console.error('❌ Error al cargar historia en formulario:', error);
            alert('Error al cargar la historia. Por favor, intente nuevamente.');
        }
    };
    
    // 🆕 Generar encabezado HTML
    const generarEncabezadoHTML = () => {
        if (!encabezadoPersonalizado) return '';
        
        const limpiarHTML = (html) => {
            if (!html) return '';
            return html
                .replace(/<p>/gi, '<div style="margin: 0; padding: 0; line-height: 1.1;">')
                .replace(/<\/p>/gi, '</div>')
                .replace(/<br\s*\/?>/gi, '<br style="line-height: 1.1;" />');
        };
        
        return `
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; background-color: #ffffff;">
                <tr>
                    <td style="width: 30%; padding: 2px; font-size: 10px; line-height: 1.1; vertical-align: top; word-wrap: break-word;">
                        ${limpiarHTML(encabezadoPersonalizado.izquierda)}
                    </td>
                    <td style="width: 40%; padding: 2px; text-align: center; font-size: 11px; line-height: 1.1; vertical-align: top; word-wrap: break-word;">
                        ${limpiarHTML(encabezadoPersonalizado.centro)}
                    </td>
                    <td style="width: 30%; padding: 2px; text-align: right; font-size: 10px; line-height: 1.1; vertical-align: top; word-wrap: break-word;">
                        ${limpiarHTML(encabezadoPersonalizado.derecha)}
                    </td>
                </tr>
            </table>
        `;
    };
    
    const generarHtmlCuerpoTablasDatosHistoria = (historia, opts = {}) => {
        const cert = Boolean(opts.espaciadoCertificado);
        // Certificado: más aire *dentro* de cada fila (celdas), no entre bloques (mbBloque como vista normal).
        const padCel = cert ? '8px 10px' : '4px 8px';
        const padTit = cert ? '6px 8px' : '6px 8px';
        const mbBloque = '8px';
        const lhValor = cert ? '1.4' : 'normal';

        let tablaDatos = '';
        try {
            const datos = typeof historia.datos === 'string' ? JSON.parse(historia.datos) : historia.datos;
            const estructura = typeof historia.plantilla_estructura === 'string' 
                ? JSON.parse(historia.plantilla_estructura) 
                : historia.plantilla_estructura;
            
            if (estructura && estructura.secciones && Array.isArray(estructura.secciones)) {
                estructura.secciones.forEach((seccion) => {
                    let seccionTieneContenido = false;
                    let filasSeccion = '';
                    
                    if (seccion.campos && Array.isArray(seccion.campos)) {
                        seccion.campos.forEach((campo) => {
                            const valor = datos[campo.id];
                            if (valor !== undefined && valor !== null && valor !== '') {
                                seccionTieneContenido = true;
                                let valorFormateado = valor;
                                if (campo.tipo === 'checkbox') {
                                    valorFormateado = valor ? 'Sí' : 'No';
                                } else if (campo.tipo === 'textarea') {
                                    valorFormateado = valor.replace(/\n/g, '<br>');
                                }
                                
                                filasSeccion += `
                                    <tr>
                                        <td style="background-color: #f8f9fa; font-weight: bold; padding: ${padCel}; width: 30%; border: 1px solid #dee2e6;">${campo.label}</td>
                                        <td style="background-color: #ffffff; padding: ${padCel}; border: 1px solid #dee2e6; line-height: ${lhValor};">${valorFormateado}</td>
                                    </tr>`;
                            }
                        });
                    }
                    
                    if (seccionTieneContenido) {
                        tablaDatos += `
                            <div style="margin-bottom: ${mbBloque};">
                                <table style="width: 100%; border-collapse: collapse; font-size: 10px; border: 1px solid #dee2e6;">
                                    <tbody>
                                        <tr>
                                            <td colspan="2" style="background-color: #f8f9fa; font-weight: bold; padding: ${padTit}; border: 1px solid #dee2e6;">${seccion.titulo}</td>
                                        </tr>
                                        ${filasSeccion}
                                    </tbody>
                                </table>
                            </div>`;
                    }
                });
            }
        } catch (error) {
            console.error('❌ Error parseando datos:', error);
            tablaDatos = `<div style="color: red; padding: 8px;">Error al cargar datos de la historia: ${error.message}</div>`;
        }
        return tablaDatos;
    };

    const generarBloqueFirmaProfesionalHTML = (historia, opts = {}) => {
        const mt = opts.margenSuperior != null ? opts.margenSuperior : 10;
        const firmaSrc = (historia.firma_final || historia.firma) || '';
        if (!historia.profesional || !firmaSrc) return '';
        const pieTexto = pieFirmaHistoriaProfesional(historia);
        const pieFirmaHtml = pieTexto
            ? `<div style="font-size: 10px; color: #6c757d; white-space: pre-line;">${escapeHtmlBasicoHc(pieTexto)}</div>`
            : `<div style="font-size: 10px; color: #6c757d;">
                    ${escapeHtmlBasicoHc(historia.profesional)}<br/>
                    <strong>RM:</strong> ${escapeHtmlBasicoHc(historia.registro_medico || 'N/A')} | <strong>Habilitación:</strong> ${escapeHtmlBasicoHc(historia.codigo_habilitacion || 'N/A')}
                  </div>`;
        return `
                <div style="margin-top: ${mt}px; page-break-inside: avoid;">
                    <div style="background-color: #f8f9fa; font-weight: bold; padding: 8px 10px; font-size: 11px; text-align: left; margin-bottom: 5px;">
                        FIRMA DEL PROFESIONAL
                    </div>
                    <table style="border-collapse: collapse;">
                        <tbody>
                            <tr>
                                <td style="padding: 5px 20px 5px 10px; vertical-align: middle;">
                                    ${pieFirmaHtml}
                                </td>
                                <td style="padding: 5px 10px; vertical-align: middle;">
                                    <img src="${firmaSrc}" alt="Firma" style="max-width: 170px; max-height: 80px; display: block;" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>`;
    };

    const generarHtmlTablaLaboralDosColumnas = (filas) => {
        if (!filas || filas.length === 0) {
            return '';
        }
        let cuerpo = '';
        for (let i = 0; i < filas.length; i += 2) {
            const a = filas[i];
            const b = filas[i + 1];
            const zebra = Math.floor(i / 2) % 2 === 0;
            const bgFila = zebra ? '#ffffff' : '#f8f9fa';
            cuerpo += `
                <tr style="background-color: ${bgFila};">
                    <td style="font-weight: bold; padding: 8px 10px; width: 22%; border: 1px solid #dee2e6; line-height: 1.35;">${escapeHtmlBasicoHc(a.etiqueta)}</td>
                    <td style="padding: 8px 10px; width: 28%; border: 1px solid #dee2e6; line-height: 1.35;">${escapeHtmlBasicoHc(String(a.valor))}</td>
                    ${
                        b
                            ? `<td style="font-weight: bold; padding: 8px 10px; width: 22%; border: 1px solid #dee2e6; line-height: 1.35;">${escapeHtmlBasicoHc(b.etiqueta)}</td>
                       <td style="padding: 8px 10px; width: 28%; border: 1px solid #dee2e6; line-height: 1.35;">${escapeHtmlBasicoHc(String(b.valor))}</td>`
                            : '<td colspan="2" style="border: 1px solid #dee2e6;"></td>'
                    }
                            </tr>`;
            }
        return `
            <div style="margin-bottom: 8px; page-break-inside: avoid;">
                <table style="width: 100%; border-collapse: collapse; font-size: 10px; border: 1px solid #dee2e6;">
                    <tbody>${cuerpo}</tbody>
                </table>
            </div>`;
    };

    const generarContenidoCertificadoConLaborHTML = (historiaCert, historiaMl) => {
        const { nombreCompleto, documentoCompleto } = obtenerDatosPaciente();
        const filasPacienteCert = obtenerFilasPacienteCertificado();
        const datosMl = historiaMl ? parseDatosHistoriaRegistro(historiaMl) : {};
        const filasLabor = filasLaboralesParaPdf(datosMl);
        const fechaBarra = fechaHistoriaSoloFechaCorta(historiaCert.fecha);

        const barraTituloYFecha = `
            <div style="margin-bottom: 8px; page-break-inside: avoid;">
                <table style="width: 100%; border-collapse: collapse; font-size: 11px; border: 1px solid #dee2e6; background-color: #e9ecef;">
                    <tbody>
                        <tr>
                            <td style="width: 50%; padding: 8px 10px; font-weight: bold; border: 1px solid #dee2e6; vertical-align: middle;">CERTIFICADO MÉDICO</td>
                            <td style="width: 50%; padding: 8px 10px; text-align: right; border: 1px solid #dee2e6; vertical-align: middle;"><strong>FECHA:</strong> ${escapeHtmlBasicoHc(fechaBarra)}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>`;

        const tablaIdentificacion = `
            <div style="margin-bottom: 8px; page-break-inside: avoid;">
                    <table style="width: 100%; border-collapse: collapse; font-size: 10px; border: 1px solid #dee2e6;">
                        <tbody>
                            <tr>
                            <td style="background-color: #f8f9fa; font-weight: bold; padding: 8px 10px; width: 28%; border: 1px solid #dee2e6;">PROFESIONAL TRATANTE</td>
                            <td style="background-color: #ffffff; padding: 8px 10px; border: 1px solid #dee2e6;">${escapeHtmlBasicoHc(historiaCert.profesional || '')}</td>
                            </tr>
                        <tr>
                            <td style="background-color: #f8f9fa; font-weight: bold; padding: 8px 10px; border: 1px solid #dee2e6;">PACIENTE</td>
                            <td style="background-color: #ffffff; padding: 8px 10px; border: 1px solid #dee2e6;">${escapeHtmlBasicoHc(nombreCompleto)}</td>
                        </tr>
                        <tr>
                            <td style="background-color: #f8f9fa; font-weight: bold; padding: 8px 10px; border: 1px solid #dee2e6;">DOCUMENTO</td>
                            <td style="background-color: #ffffff; padding: 8px 10px; border: 1px solid #dee2e6;">${escapeHtmlBasicoHc(documentoCompleto)}</td>
                        </tr>
                        ${filasPacienteCert.map((fila) => `
                        <tr>
                            <td style="background-color: #f8f9fa; font-weight: bold; padding: 8px 10px; border: 1px solid #dee2e6;">${escapeHtmlBasicoHc(fila.etiqueta)}</td>
                            <td style="background-color: #ffffff; padding: 8px 10px; border: 1px solid #dee2e6;">${escapeHtmlBasicoHc(fila.valor)}</td>
                        </tr>`).join('')}
                        </tbody>
                    </table>
                </div>`;

        return (
            generarEncabezadoHTML() +
            barraTituloYFecha +
            tablaIdentificacion +
            generarHtmlTablaLaboralDosColumnas(filasLabor) +
            generarHtmlCuerpoTablasDatosHistoria(historiaCert, { espaciadoCertificado: true }) +
            generarBloqueFirmaProfesionalHTML(historiaCert)
        );
    };

    const construirSeccionesCuerpoHistoriaPdf = (historia) => {
        const out = [];
        try {
            const datos = typeof historia.datos === 'string' ? JSON.parse(historia.datos) : historia.datos;
            const estructura = typeof historia.plantilla_estructura === 'string'
                ? JSON.parse(historia.plantilla_estructura)
                : historia.plantilla_estructura;

            if (estructura && estructura.secciones && Array.isArray(estructura.secciones)) {
                estructura.secciones.forEach((seccion) => {
                    const filasSeccion = [];
                    if (seccion.campos && Array.isArray(seccion.campos)) {
                        seccion.campos.forEach((campo) => {
                            const valor = datos[campo.id];
                            if (valor !== undefined && valor !== null && valor !== '') {
                                let valorFormateado = valor;
                                if (campo.tipo === 'checkbox') {
                                    valorFormateado = valor ? 'Sí' : 'No';
                                } else if (campo.tipo === 'textarea') {
                                    valorFormateado = String(valor).replace(/\r\n/g, '\n');
                                }
                                filasSeccion.push({
                                    etiqueta: campo.label,
                                    valor: valorFormateado
                                });
                            }
                        });
                    }
                    if (filasSeccion.length > 0) {
                        out.push({
                            titulo: seccion.titulo,
                            tipo: 'banner',
                            contenido: seccion.titulo
                        });
                        out.push({
                            tipo: 'tabla-datos',
                            datos: { filas: filasSeccion }
                        });
                    }
                });
            }
        } catch (error) {
            console.error('Error parseando datos de historia para PDF:', error);
        }
        return out;
    };

    const construirSeccionFirmaPdf = (historia) => {
        const firmaSrcPdf = (historia.firma_final || historia.firma) || '';
        if (!historia.profesional || !firmaSrcPdf) return null;
        const piePdf = pieFirmaHistoriaProfesional(historia);
        const firmaMetadata = piePdf
            ? { tipo: 'profesional', textoPieFirma: piePdf }
            : {
                  tipo: 'profesional',
                  nombre: historia.profesional || '',
                  registroMedico: historia.registro_medico || 'N/A',
                  codigoHabilitacion: historia.codigo_habilitacion || 'N/A'
              };
        return {
            tipo: 'firma',
            imagenBase64: firmaSrcPdf,
            metadata: firmaMetadata
        };
    };

    const construirSeccionesPdfUnaHistoria = (historia) => {
        const secciones = [];
        const nombrePlantilla = historia.plantilla_nombre || 'Historia Clínica';

        secciones.push({
            titulo: nombrePlantilla.toUpperCase(),
            tipo: 'banner',
            contenido: nombrePlantilla.toUpperCase()
        });

        secciones.push({
            tipo: 'tabla-datos',
            datos: {
                filas: [
                    { etiqueta: 'FECHA Y HORA', valor: fechaHistoriaParaDisplay(historia.fecha) },
                    { etiqueta: 'PROFESIONAL TRATANTE', valor: String(historia.profesional || '') }
                ]
            }
        });

        secciones.push(...construirSeccionesCuerpoHistoriaPdf(historia));
        secciones.push(...construirSeccionesNotasAclaratoriasPdf(historia.id));

        const firmaSec = construirSeccionFirmaPdf(historia);
        if (firmaSec) secciones.push(firmaSec);

        return secciones;
    };

    const construirConfigPdfConsolidado = () => {
        if (!historialSoloGuardadas.length) return null;

        const { nombreCompleto, documentoCompleto } = obtenerDatosPaciente();
        const docPaciente = paciente?.numeroDocumento || paciente?.numero_documento || 'paciente';

        const config = {
            filename: `historias-clinicas-completo-${docPaciente}.pdf`,
            paciente: {
                nombreCompleto,
                documentoCompleto
            },
            encabezadoPersonalizado: encabezadoPersonalizado
                ? {
                      izquierda: encabezadoPersonalizado.izquierda || '',
                      centro: encabezadoPersonalizado.centro || '',
                      derecha: encabezadoPersonalizado.derecha || ''
                  }
                : null,
            secciones: [],
            firmas: []
        };

        historialSoloGuardadas.forEach((historia, index) => {
            if (index > 0) {
                config.secciones.push({ tipo: 'salto-pagina' });
            }
            config.secciones.push(...construirSeccionesPdfUnaHistoria(historia));
        });

        return config;
    };

    const construirConfigPdfHistoriaIndividual = (historia) => {
        if (!historia) return null;

        const { nombreCompleto, documentoCompleto } = obtenerDatosPaciente();
        const docPaciente = paciente?.numeroDocumento || paciente?.numero_documento || 'paciente';

        return {
            filename: `historia-clinica-${docPaciente}.pdf`,
            paciente: {
                nombreCompleto,
                documentoCompleto
            },
            encabezadoPersonalizado: encabezadoPersonalizado
                ? {
                      izquierda: encabezadoPersonalizado.izquierda || '',
                      centro: encabezadoPersonalizado.centro || '',
                      derecha: encabezadoPersonalizado.derecha || ''
                  }
                : null,
            secciones: construirSeccionesPdfUnaHistoria(historia),
            firmas: []
        };
    };

    const blobPdfToBase64 = (blob) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result;
                if (!result || typeof result !== 'string') {
                    reject(new Error('No se pudo leer el PDF generado'));
                    return;
                }
                resolve(result.split(',')[1]);
            };
            reader.onerror = () => reject(new Error('Error al convertir el PDF'));
            reader.readAsDataURL(blob);
        });

    const obtenerProfesionalesParaEmail = (tipo) => {
        if (tipo === 'individual' && historiaSeleccionada) {
            return String(historiaSeleccionada.profesional || '').trim();
        }
        const nombres = [
            ...new Set(
                historialSoloGuardadas
                    .map((h) => String(h.profesional || '').trim())
                    .filter(Boolean)
            )
        ];
        return nombres.join(', ');
    };

    const obtenerTituloDocumentoEmail = (tipo) => {
        if (tipo === 'individual' && historiaSeleccionada) {
            const nombre = historiaSeleccionada.plantilla_nombre || 'Historia clínica';
            return `la historia clínica: ${nombre}`;
        }
        return 'el resumen completo de historias clínicas';
    };

    const abrirModalEmailHistoria = (tipo) => {
        if (tipo === 'consolidado' && !historialSoloGuardadas.length) return;
        if (tipo === 'individual' && !historiaSeleccionada) return;

        setEmailDestinoHistoria((paciente?.email || '').trim());
        setTipoEmailHistoria(tipo);
        setShowModalEmailHistoria(true);
    };

    const enviarHistoriaPorEmail = async () => {
        const email = (emailDestinoHistoria || '').trim();
        if (!email) {
            alert('Por favor ingrese un correo electrónico válido.');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            alert('Por favor ingrese un correo electrónico válido.');
            return;
        }

        const config =
            tipoEmailHistoria === 'individual'
                ? construirConfigPdfHistoriaIndividual(historiaSeleccionada)
                : construirConfigPdfConsolidado();

        if (!config) {
            alert('No hay contenido disponible para enviar por correo.');
            return;
        }

        try {
            setEnviandoEmailHistoria(true);
            setShowModalEmailHistoria(false);

            const pdf = await generarPdfConTextoNativo(config);
            const pdfBlob = pdf.output('blob');
            const base64data = await blobPdfToBase64(pdfBlob);

            await axios.post('/enviar-historia-clinica-email', {
                paciente_id: pacienteId,
                email_destino: email,
                pdf_base64: base64data,
                nombre_archivo: config.filename,
                titulo_documento: obtenerTituloDocumentoEmail(tipoEmailHistoria),
                nombre_profesional: obtenerProfesionalesParaEmail(tipoEmailHistoria)
            });

            alert(`Historia clínica enviada exitosamente a ${email}`);
        } catch (error) {
            console.error('Error enviando historia clínica por email:', error.response?.data || error);
            const mensaje =
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Error desconocido';
            alert('Error al enviar la historia clínica por email: ' + mensaje);
        } finally {
            setEnviandoEmailHistoria(false);
        }
    };

    const abrirPdfEnVentanaImpresion = async (config) => {
        const pdf = await generarPdfConTextoNativo(config);
        const blob = pdf.output('blob');
        const url = URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');

        if (printWindow) {
            printWindow.onload = function () {
                setTimeout(() => printWindow.print(), 500);
            };
            return;
        }

        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.src = url;
        document.body.appendChild(iframe);

        iframe.onload = function () {
            setTimeout(() => {
                try {
                    iframe.contentWindow.print();
                } catch (e) {
                    alert('No se pudo abrir el diálogo de impresión. Por favor, descarga el PDF.');
                }
            }, 500);
        };
    };

    const descargarPdfConsolidado = async () => {
        if (!historialSoloGuardadas.length) return;
        try {
            const config = construirConfigPdfConsolidado();
            if (!config) return;
            const pdf = await generarPdfConTextoNativo(config);
            pdf.save(config.filename);
        } catch (error) {
            console.error('Error generando PDF consolidado:', error);
            alert('Error al generar el PDF. Por favor, intenta nuevamente.');
        }
    };

    const imprimirConsolidado = async () => {
        if (!historialSoloGuardadas.length) return;
        try {
            const config = construirConfigPdfConsolidado();
            if (!config) return;
            await abrirPdfEnVentanaImpresion(config);
        } catch (error) {
            console.error('Error imprimiendo PDF consolidado:', error);
            alert('Error al generar el PDF. Por favor, intenta nuevamente.');
        }
    };

    const PADDING_CELDAS_CUERPO_CERT_PDF = { top: 1.5, bottom: 1.5, left: 1.2, right: 1.2 };

    /** Solo más padding *dentro* de las filas de las tablas de cuerpo; no aumenta saltos entre secciones. */
    const aplicarPaddingCeldasCuerpoCertificadoPdf = (seccionesCuerpo) =>
        (seccionesCuerpo || []).map((s) => {
            if (s.tipo !== 'tabla-datos') return s;
            return {
                ...s,
                estilos: {
                    ...s.estilos,
                    bodyStyles: {
                        ...s.estilos?.bodyStyles,
                        cellPadding: { ...PADDING_CELDAS_CUERPO_CERT_PDF, ...s.estilos?.bodyStyles?.cellPadding }
                    }
                }
            };
        });

    const construirConfigPdfCertificadoLabor = (hCert, hMl) => {
        const { nombreCompleto, documentoCompleto } = obtenerDatosPaciente();
        const filasPacienteCert = obtenerFilasPacienteCertificado();
        const datosMl = hMl ? parseDatosHistoriaRegistro(hMl) : {};
        const filasLab = filasLaboralesParaPdf(datosMl);
        const docPaciente = paciente?.numeroDocumento || 'paciente';
        const fechaBarra = fechaHistoriaSoloFechaCorta(hCert.fecha);

        const secciones = [
            {
                tipo: 'tabla-datos',
                datos: {
                    filas: [
                        {
                            etiqueta: 'CERTIFICADO MÉDICO',
                            valor: `FECHA: ${fechaBarra}`
                        }
                    ]
                },
                estilos: {
                    columnStyles: {
                        0: { cellWidth: 95, fontStyle: 'bold', fillColor: [233, 236, 239], halign: 'left' },
                        1: { cellWidth: 'auto', fillColor: [233, 236, 239], halign: 'right' }
                    },
                    bodyStyles: {
                        fillColor: [233, 236, 239],
                        cellPadding: { top: 1.2, bottom: 1.2, left: 1.4, right: 1.4 }
                    },
                    alternateRowStyles: { fillColor: [233, 236, 239] },
                    postTablaMm: 2
                }
            },
            {
                tipo: 'tabla-datos',
                datos: {
                    filas: [
                        { etiqueta: 'PROFESIONAL TRATANTE', valor: String(hCert.profesional || '') },
                        { etiqueta: 'PACIENTE', valor: nombreCompleto },
                        { etiqueta: 'DOCUMENTO', valor: documentoCompleto },
                        ...filasPacienteCert
                    ]
                },
                estilos: {
                    postTablaMm: 2,
                    bodyStyles: { cellPadding: { top: 1.5, bottom: 1.5, left: 1.3, right: 1.3 } }
                }
            }
        ];

        if (filasLab.length > 0) {
            secciones.push({
                tipo: 'tabla-datos',
                datos: { filas: filasLab },
                estilos: {
                    postTablaMm: 2,
                    bodyStyles: { cellPadding: { top: 1.5, bottom: 1.5, left: 1.2, right: 1.2 } }
                }
            });
        }

        secciones.push(...aplicarPaddingCeldasCuerpoCertificadoPdf(construirSeccionesCuerpoHistoriaPdf(hCert)));
        const firmaSec = construirSeccionFirmaPdf(hCert);
        if (firmaSec) secciones.push(firmaSec);

        return {
            filename: `certificado-medico-${docPaciente}.pdf`,
            paciente: {
                nombreCompleto,
                documentoCompleto
            },
            encabezadoPersonalizado: encabezadoPersonalizado
                ? {
                      izquierda: encabezadoPersonalizado.izquierda || '',
                      centro: encabezadoPersonalizado.centro || '',
                      derecha: encabezadoPersonalizado.derecha || ''
                  }
                : null,
            omitTablaPacienteInicial: true,
            secciones,
            firmas: []
        };
    };

    const abrirModalSeleccionMedicinaLaboral = () => {
        if (!puedeGenerarCertificado) return;
        if (historialesMedicinaLaboral.length === 0) {
            setRegistroMedicinaLaboralElegido(null);
            setShowModalVistaCertificadoLabor(true);
            return;
        }
        setRegistroMedicinaLaboralElegido(historialesMedicinaLaboral[0]);
        setShowModalElegirMedicinaLaboral(true);
    };

    const descargarPdfCertificadoLabor = async () => {
        if (!historiaSeleccionada) return;
        try {
            const config = construirConfigPdfCertificadoLabor(
                historiaSeleccionada,
                registroMedicinaLaboralElegido
            );
            const pdf = await generarPdfConTextoNativo(config);
            pdf.save(config.filename);
        } catch (error) {
            console.error('Error generando PDF certificado:', error);
            alert('Error al generar el PDF del certificado. Por favor, intente nuevamente.');
        }
    };

    const imprimirCertificadoLabor = async () => {
        if (!historiaSeleccionada) return;
        try {
            const config = construirConfigPdfCertificadoLabor(
                historiaSeleccionada,
                registroMedicinaLaboralElegido
            );
            const pdf = await generarPdfConTextoNativo(config);
            const blob = pdf.output('blob');
            const url = URL.createObjectURL(blob);
            const printWindow = window.open(url, '_blank');
            if (printWindow) {
                printWindow.onload = function () {
                    setTimeout(() => printWindow.print(), 500);
                };
            } else {
                const iframe = document.createElement('iframe');
                iframe.style.position = 'fixed';
                iframe.style.width = '0';
                iframe.style.height = '0';
                iframe.src = url;
                document.body.appendChild(iframe);
                iframe.onload = function () {
                    setTimeout(() => {
                        try {
                            iframe.contentWindow.print();
                        } catch (e) {
                            alert('No se pudo imprimir. Descargue el PDF.');
                        }
                    }, 500);
                };
            }
        } catch (error) {
            console.error('Error imprimiendo certificado:', error);
            alert('Error al preparar la impresión.');
        }
    };
    
    // 🆕 Generar contenido HTML de la historia (ESTILO TABLAS CON BANDAS GRISES)
    const generarContenidoHistoriaHTML = (historia) => {
        console.log('📄 Generando HTML para historia:', historia);
        
        // 🆕 Usar helper para obtener datos del paciente
        const { nombreCompleto, documentoCompleto } = obtenerDatosPaciente();
        
        // 🆕 Obtener nombre de la plantilla
        const nombrePlantilla = historia.plantilla_nombre || 'Historia Clínica';
        
        // 1. BANNER: NOMBRE DE LA PLANTILLA (centrado y en negrita)
        let banner = `
            <div style="margin-bottom: 8px; page-break-inside: avoid;">
                <div style="background-color: #e9ecef; padding: 6px 8px; font-weight: bold; font-size: 12px; text-align: center; border: 1px solid #dee2e6;">
                    ${nombrePlantilla.toUpperCase()}
                </div>
            </div>`;
        
        // 2. Información del Paciente (tabla con bandas grises - sin PLANTILLA)
        let tablaInfoPaciente = `
            <div style="margin-bottom: 8px; page-break-inside: avoid;">
                <table style="width: 100%; border-collapse: collapse; font-size: 10px; border: 1px solid #dee2e6;">
                    <tbody>
                        <tr>
                            <td style="background-color: #f8f9fa; font-weight: bold; padding: 4px 8px; width: 25%; border: 1px solid #dee2e6;">PACIENTE</td>
                            <td style="background-color: #ffffff; padding: 4px 8px; border: 1px solid #dee2e6;">${nombreCompleto}</td>
                        </tr>
                        <tr>
                            <td style="background-color: #f8f9fa; font-weight: bold; padding: 4px 8px; border: 1px solid #dee2e6;">DOCUMENTO</td>
                            <td style="background-color: #ffffff; padding: 4px 8px; border: 1px solid #dee2e6;">${documentoCompleto}</td>
                        </tr>
                        <tr>
                            <td style="background-color: #f8f9fa; font-weight: bold; padding: 4px 8px; border: 1px solid #dee2e6;">FECHA Y HORA</td>
                            <td style="background-color: #ffffff; padding: 4px 8px; border: 1px solid #dee2e6;">${fechaHistoriaParaDisplay(historia.fecha)}</td>
                        </tr>
                        <tr>
                            <td style="background-color: #f8f9fa; font-weight: bold; padding: 4px 8px; border: 1px solid #dee2e6;">PROFESIONAL TRATANTE</td>
                            <td style="background-color: #ffffff; padding: 4px 8px; border: 1px solid #dee2e6; font-weight: bold; color: #325e68;">${historia.profesional || ''}</td>
                        </tr>
                    </tbody>
                </table>
            </div>`;
        
        return banner + tablaInfoPaciente + generarHtmlCuerpoTablasDatosHistoria(historia) + htmlNotasAclaratoriasRegistro(historia.id) + generarBloqueFirmaProfesionalHTML(historia);
    };
    
    // 🆕 Descargar PDF usando jsPDF con texto nativo
    const descargarPdfHistoria = async () => {
        if (!historiaSeleccionada) return;
        
        try {
            // 🆕 Usar helper para obtener datos del paciente
            const { nombreCompleto, documentoCompleto } = obtenerDatosPaciente();
            const docPaciente = paciente?.numeroDocumento || 'paciente';
            
            // 🆕 Obtener nombre de la plantilla
            const nombrePlantilla = historiaSeleccionada.plantilla_nombre || 'Historia Clínica';
            
            // Preparar configuración para generarPdfConTextoNativo
            const config = {
                filename: 'historia-clinica-' + docPaciente + '.pdf',
                paciente: {
                    nombreCompleto: nombreCompleto,
                    documentoCompleto: documentoCompleto
                },
                encabezadoPersonalizado: encabezadoPersonalizado ? {
                    izquierda: encabezadoPersonalizado.izquierda || '',
                    centro: encabezadoPersonalizado.centro || '',
                    derecha: encabezadoPersonalizado.derecha || ''
                } : null,
                secciones: [],
                firmas: []
            };
            
            // Banner de título con nombre de plantilla
            config.secciones.push({
                titulo: nombrePlantilla.toUpperCase(),
                tipo: 'banner',
                contenido: nombrePlantilla.toUpperCase()
            });
            
            // Tabla de fecha
            config.secciones.push({
                tipo: 'tabla-datos',
                datos: {
                    filas: [
                        { etiqueta: 'FECHA Y HORA', valor: fechaHistoriaParaDisplay(historiaSeleccionada.fecha) },
                        { etiqueta: 'PROFESIONAL TRATANTE', valor: String(historiaSeleccionada.profesional || '') }
                    ]
                }
            });
            
            // Procesar datos de la historia (secciones con tablas)
            try {
                const datos = typeof historiaSeleccionada.datos === 'string' 
                    ? JSON.parse(historiaSeleccionada.datos) 
                    : historiaSeleccionada.datos;
                const estructura = typeof historiaSeleccionada.plantilla_estructura === 'string' 
                    ? JSON.parse(historiaSeleccionada.plantilla_estructura) 
                    : historiaSeleccionada.plantilla_estructura;
                
                if (estructura && estructura.secciones && Array.isArray(estructura.secciones)) {
                    estructura.secciones.forEach(seccion => {
                        // Verificar si la sección tiene campos con valores
                        const filasSeccion = [];
                        
                        if (seccion.campos && Array.isArray(seccion.campos)) {
                            seccion.campos.forEach(campo => {
                                const valor = datos[campo.id];
                                if (valor !== undefined && valor !== null && valor !== '') {
                                    // Formatear valor según tipo de campo
                                    let valorFormateado = valor;
                                    if (campo.tipo === 'checkbox') {
                                        valorFormateado = valor ? 'Sí' : 'No';
                                    } else if (campo.tipo === 'textarea') {
                                        valorFormateado = String(valor).replace(/\r\n/g, '\n');
                                    }
                                    
                                    filasSeccion.push({
                                        etiqueta: campo.label,
                                        valor: valorFormateado
                                    });
                                }
                            });
                        }
                        
                        // Solo agregar sección si tiene contenido
                        if (filasSeccion.length > 0) {
                            // Agregar título de sección como banner
                            config.secciones.push({
                                titulo: seccion.titulo,
                                tipo: 'banner',
                                contenido: seccion.titulo
                            });
                            
                            // Agregar datos de la sección
                            config.secciones.push({
                                tipo: 'tabla-datos',
                                datos: {
                                    filas: filasSeccion
                                }
                            });
                        }
                    });
                }
            } catch (error) {
                console.error('Error parseando datos de historia:', error);
            }
            
            config.secciones.push(...construirSeccionesNotasAclaratoriasPdf(historiaSeleccionada.id));

            const firmaSrcPdf = (historiaSeleccionada.firma_final || historiaSeleccionada.firma) || '';
            if (historiaSeleccionada.profesional && firmaSrcPdf) {
                const piePdf = pieFirmaHistoriaProfesional(historiaSeleccionada);
                const firmaMetadata = piePdf
                    ? { tipo: 'profesional', textoPieFirma: piePdf }
                    : {
                        tipo: 'profesional',
                        nombre: historiaSeleccionada.profesional || '',
                        registroMedico: historiaSeleccionada.registro_medico || 'N/A',
                        codigoHabilitacion: historiaSeleccionada.codigo_habilitacion || 'N/A'
                    };
                config.secciones.push({
                    tipo: 'firma',
                    imagenBase64: firmaSrcPdf,
                    metadata: firmaMetadata
                });
            }
            
            // Generar y descargar PDF
            const pdf = await generarPdfConTextoNativo(config);
            pdf.save(config.filename);
            
        } catch (error) {
            console.error('Error generando PDF:', error);
            alert('Error al generar el PDF. Por favor, intenta nuevamente.');
        }
    };
    
    // 🆕 Imprimir PDF usando jsPDF con texto nativo
    const imprimirHistoria = async () => {
        if (!historiaSeleccionada) return;
        
        try {
            // 🆕 Usar helper para obtener datos del paciente
            const { nombreCompleto, documentoCompleto } = obtenerDatosPaciente();
            const docPaciente = paciente?.numeroDocumento || 'paciente';
            
            // 🆕 Obtener nombre de la plantilla
            const nombrePlantilla = historiaSeleccionada.plantilla_nombre || 'Historia Clínica';
            
            // Preparar configuración para generarPdfConTextoNativo
            const config = {
                filename: 'historia-clinica-' + docPaciente + '.pdf',
                paciente: {
                    nombreCompleto: nombreCompleto,
                    documentoCompleto: documentoCompleto
                },
                encabezadoPersonalizado: encabezadoPersonalizado ? {
                    izquierda: encabezadoPersonalizado.izquierda || '',
                    centro: encabezadoPersonalizado.centro || '',
                    derecha: encabezadoPersonalizado.derecha || ''
                } : null,
                secciones: [],
                firmas: []
            };
            
            // Banner de título con nombre de plantilla
            config.secciones.push({
                titulo: nombrePlantilla.toUpperCase(),
                tipo: 'banner',
                contenido: nombrePlantilla.toUpperCase()
            });
            
            // Tabla de fecha
            config.secciones.push({
                tipo: 'tabla-datos',
                datos: {
                    filas: [
                        { etiqueta: 'FECHA Y HORA', valor: fechaHistoriaParaDisplay(historiaSeleccionada.fecha) },
                        { etiqueta: 'PROFESIONAL TRATANTE', valor: String(historiaSeleccionada.profesional || '') }
                    ]
                }
            });
            
            // Procesar datos de la historia (secciones con tablas)
            try {
                const datos = typeof historiaSeleccionada.datos === 'string' 
                    ? JSON.parse(historiaSeleccionada.datos) 
                    : historiaSeleccionada.datos;
                const estructura = typeof historiaSeleccionada.plantilla_estructura === 'string' 
                    ? JSON.parse(historiaSeleccionada.plantilla_estructura) 
                    : historiaSeleccionada.plantilla_estructura;
                
                if (estructura && estructura.secciones && Array.isArray(estructura.secciones)) {
                    estructura.secciones.forEach(seccion => {
                        // Verificar si la sección tiene campos con valores
                        const filasSeccion = [];
                        
                        if (seccion.campos && Array.isArray(seccion.campos)) {
                            seccion.campos.forEach(campo => {
                                const valor = datos[campo.id];
                                if (valor !== undefined && valor !== null && valor !== '') {
                                    // Formatear valor según tipo de campo
                                    let valorFormateado = valor;
                                    if (campo.tipo === 'checkbox') {
                                        valorFormateado = valor ? 'Sí' : 'No';
                                    } else if (campo.tipo === 'textarea') {
                                        valorFormateado = String(valor).replace(/\r\n/g, '\n');
                                    }
                                    
                                    filasSeccion.push({
                                        etiqueta: campo.label,
                                        valor: valorFormateado
                                    });
                                }
                            });
                        }
                        
                        // Solo agregar sección si tiene contenido
                        if (filasSeccion.length > 0) {
                            // Agregar título de sección como banner
                            config.secciones.push({
                                titulo: seccion.titulo,
                                tipo: 'banner',
                                contenido: seccion.titulo
                            });
                            
                            // Agregar datos de la sección
                            config.secciones.push({
                                tipo: 'tabla-datos',
                                datos: {
                                    filas: filasSeccion
                                }
                            });
                        }
                    });
                }
            } catch (error) {
                console.error('Error parseando datos de historia:', error);
            }
            
            config.secciones.push(...construirSeccionesNotasAclaratoriasPdf(historiaSeleccionada.id));

            const firmaSrcPdf = (historiaSeleccionada.firma_final || historiaSeleccionada.firma) || '';
            if (historiaSeleccionada.profesional && firmaSrcPdf) {
                const piePdfImp = pieFirmaHistoriaProfesional(historiaSeleccionada);
                const firmaMetadata = piePdfImp
                    ? { tipo: 'profesional', textoPieFirma: piePdfImp }
                    : {
                        tipo: 'profesional',
                        nombre: historiaSeleccionada.profesional || '',
                        registroMedico: historiaSeleccionada.registro_medico || 'N/A',
                        codigoHabilitacion: historiaSeleccionada.codigo_habilitacion || 'N/A'
                    };
                config.secciones.push({
                    tipo: 'firma',
                    imagenBase64: firmaSrcPdf,
                    metadata: firmaMetadata
                });
            }
            
            // Generar PDF
            const pdf = await generarPdfConTextoNativo(config);
            
            // Abrir diálogo de impresión
            const blob = pdf.output('blob');
            const url = URL.createObjectURL(blob);
            const printWindow = window.open(url, '_blank');
            
            if (printWindow) {
                printWindow.onload = function() {
                    setTimeout(() => printWindow.print(), 500);
                };
            } else {
                // Fallback: iframe oculto
                console.log('Popup bloqueado, usando método alternativo');
                const iframe = document.createElement('iframe');
                iframe.style.position = 'fixed';
                iframe.style.width = '0';
                iframe.style.height = '0';
                iframe.src = url;
                document.body.appendChild(iframe);
                
                iframe.onload = function() {
                    setTimeout(() => {
                        try {
                            iframe.contentWindow.print();
                        } catch (e) {
                            alert('No se pudo abrir el diálogo de impresión. Por favor, descarga el PDF.');
                        }
                    }, 500);
                };
            }
        } catch (error) {
            console.error('Error generando PDF:', error);
            alert('Error al generar el PDF. Por favor, intenta nuevamente.');
        }
    };
    
    // 🆕 Funciones del modal de confirmación
    const handleCerrarModalConfirmacion = () => {
        setShowModalConfirmacion(false);
    };
    
    // 🆕 Generar vista consolidada de todas las historias
    const generarVistaConsolidada = () => {
        if (!historialSoloGuardadas.length) {
            alert('No hay historias guardadas para mostrar');
            return;
        }
        
        let consolidadoHTML = '';
        
        historialSoloGuardadas.forEach((historia, index) => {
            // Forzar que cada historia inicie en hoja nueva (excepto la primera)
            if (index > 0) {
                consolidadoHTML += '<div style="page-break-before: always; height: 0; margin: 0; padding: 0;"></div>';
            }
            
            // Generar contenido de cada historia
            consolidadoHTML += generarContenidoHistoriaHTML(historia);
        });
        
        setConsolidado(consolidadoHTML);
        setShowModalConsolidado(true);
    };
    
    // 🔒 Verificación de permisos
    if (hasPermission === false) {
        return <Unauthorized />;
    }

    if (hasPermission === null) {
        return (
            <div className="hold-transition">
                <Loading />
            </div>
        );
    }
    
    // 🆕 Validar que hay pacienteId
    if (!paciente_id) {
        return (
            <>
                <Header />
                <Aside />
                <div className="content-wrapper">
                    <section className="content">
                        <div className="container-fluid">
                            <div className="alert alert-danger">
                                <i className="fas fa-exclamation-triangle"></i>
                                {' '}No se especificó un paciente. Por favor, vuelva e intente nuevamente.
                            </div>
                        </div>
                    </section>
                </div>
                <Footer />
            </>
        );
    }
    
    // 🆕 Mostrar loading mientras carga paciente
    if (loadingPaciente) {
        return <Loading />;
    }
    
    return (
        <>
            <style>{`
                /* Corregir comportamiento de hover en botones */
                .btn-primary, .btn-secondary {
                    transform: scale(1.05);
                    transition: transform 0.2s ease;
                }
                .btn-primary:hover, .btn-secondary:hover {
                    transform: scale(1);
                }
                .btn-primary:active, .btn-secondary:active {
                    transform: scale(0.98);
                }
                .timeline-item-container.timeline-borrador .timeline-meta {
                    background-color: #fff3cd !important;
                    border-color: #ffc107 !important;
                }
                .timeline-item-container.timeline-borrador:hover .timeline-meta {
                    background-color: #ffecb5 !important;
                    border-color: #e0a800 !important;
                }
                .timeline-item-container.timeline-borrador.selected .timeline-meta {
                    background-color: #ffecb5 !important;
                    border: 2px solid #ffc107 !important;
                    box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.2);
                }
                .timeline-item-container.selected:not(.timeline-borrador) .timeline-meta {
                    background-color: #d1ecf1 !important;
                    border: 2px solid #17a2b8 !important;
                    color: #17a2b8;
                }
                .timeline-historia-clinica .timeline-meta {
                    flex-direction: column;
                    align-items: flex-start;
                    justify-content: center;
                    min-height: auto;
                    padding: 8px 10px;
                    gap: 2px;
                }
                .timeline-historia-clinica .timeline-meta-fecha {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    width: 100%;
                    font-weight: bold;
                    font-size: 11px;
                    line-height: 1.2;
                    flex-wrap: nowrap;
                }
                .timeline-historia-clinica .timeline-meta-plantilla {
                    width: 100%;
                    max-width: 100%;
                    color: #6c757d;
                    font-size: 8px;
                    line-height: 1.15;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    margin-top: 2px;
                }
            `}</style>
            <Header />
            <Aside 
                activelink="patients"
                atencionPaciente={true}
                abrirArchivosClinicos={abrirArchivosClinicos}
                abrirEvolucion={abrirEvolucion}
                abrirHistoriaClinicaJson={() => history.push(`/historia-clinica-json?id=${paciente_id}`)}
                abrirExamenes={abrirExamenes}
                abrirFormulas={abrirFormulas}
                abrirIncapacidades={abrirIncapacidades}
                abrirRecomendaciones={abrirRecomendaciones}
                abrirRemisiones={abrirRemisiones}
                abrirContraremisiones={abrirContraremisiones}
                abrirConsentimientos={abrirConsentimientos}
                abrirRipsFev={() => setShowRipsFev(true)}
                abrirRipsSinFev={() => setShowRipsSinFev(true)}
                abrirRipsConvenio={abrirRipsConvenio}
                abrirOdontograma={abrirOdontograma}
                abrirToxina={abrirToxina}
                esFacturador={esFacturador}
                tienePermisoDocumentos={tienePermisoDocumentos}
                tienePermisoRips={tienePermisoRips}
                tienePermisoOdontograma={tienePermisoOdontograma}
                tienePermisoToxina={tienePermisoToxina}
                tienePermisoEvolucion={tienePermisoEvolucion}
                tienePermisoHistoriaClinica={tienePermisoHistoriaClinica}
            />
            <div className="content-wrapper">
                <section className="content">
                    <div className="container-fluid">
                        {/* 🆕 Título y Botones de Navegación */}
                        <div className="card mb-3" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #343a40' }}>
                            <div className="card-body" style={{ padding: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <h4 className="mb-0" style={{ color: '#343a40' }}>
                                            <i className="fas fa-file-medical"></i> HISTORIA CLÍNICA DEL PACIENTE
                                        </h4>
                                        {/* Botones de navegación rápida */}
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-dark"
                                            onClick={() => history.push(`/atender-paciente?id=${paciente_id}`)}
                                            title="Volver a Atención del Paciente"
                                            style={{ padding: '4px 8px' }}
                                        >
                                            <i className="fas fa-user-injured"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-info"
                                            onClick={() => history.push(`/estado-cuenta-paciente?id=${paciente_id}`)}
                                            title="Ver Cartera del Paciente"
                                            style={{ padding: '4px 8px' }}
                                        >
                                            <i className="fas fa-dollar-sign"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => history.goBack()}
                                            title="Volver"
                                            style={{ padding: '4px 8px' }}
                                        >
                                            <i className="fas fa-arrow-left"></i>
                                        </button>
                                    </div>
                                </div>
                                
                                {/* 🆕 Información del paciente debajo del título */}
                                <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                                    {(() => {
                                        const { nombreCompleto, documentoCompleto } = obtenerDatosPaciente();
                                        return (
                                            <div style={{ display: 'flex', gap: '30px', fontSize: '14px' }}>
                                                <div>
                                                    <strong><i className="fas fa-user"></i> Paciente:</strong> {nombreCompleto}
                                                </div>
                                                <div>
                                                    <strong><i className="fas fa-id-card"></i> Documento:</strong> {documentoCompleto}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                        
                        {/* 🆕 Botones de Acción */}
                        <div className="card mb-3">
                            <div className="card-body">
                                <button 
                                    className="btn btn-success btn-sm mr-2"
                                    onClick={nuevaHistoria}
                                >
                                    <i className="fas fa-plus"></i> Nueva Historia
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-nota-aclaratoria btn-sm mr-2"
                                    onClick={() => {
                                        if (botonNotaDisabled) {
                                            if (!historiaSeleccionada) {
                                                alert('Seleccione una historia del historial.');
                                            } else if (esHistoriaBorrador(historiaSeleccionada)) {
                                                alert('No aplica en borradores.');
                                            } else if (!tieneNotasEnRegistro && !puedeAgregarNotaAclaratoria) {
                                                alert('Solo el profesional autor puede agregar una nota aclaratoria.');
                                            }
                                            return;
                                        }
                                        setShowModalNotaAclaratoria(true);
                                    }}
                                    disabled={botonNotaDisabled}
                                    title={
                                        !historiaSeleccionada
                                            ? 'Seleccione una historia del historial'
                                            : esHistoriaBorrador(historiaSeleccionada)
                                                ? 'No aplica en borradores'
                                                : tieneNotasEnRegistro
                                                    ? 'Consultar Notas Aclaratorias'
                                                    : !puedeAgregarNotaAclaratoria
                                                        ? 'Solo el profesional autor puede agregar la nota'
                                                        : 'Agregar Nota Aclaratoria'
                                    }
                                >
                                    <i className={`fas ${tieneNotasEnRegistro ? 'fa-search' : 'fa-comment-medical'}`}></i>{' '}
                                    {tieneNotasEnRegistro ? 'Consultar Nota' : 'Nota Aclaratoria'}
                                </button>
                                <button 
                                    className="btn btn-info btn-sm"
                                    onClick={() => historiaSeleccionada && !esHistoriaBorrador(historiaSeleccionada) && setShowModalVistaPrevia(true)}
                                    disabled={!historiaSeleccionada || esHistoriaBorrador(historiaSeleccionada)}
                                    title={esHistoriaBorrador(historiaSeleccionada) ? 'Los borradores no tienen vista previa de impresión' : ''}
                                >
                                    <i className="fas fa-eye"></i> Ver Resumen
                                </button>
                                {tienePlantillasCertificadoEnCliente && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm ml-2"
                                        disabled={!puedeGenerarCertificado}
                                        title={
                                            puedeGenerarCertificado
                                                ? ''
                                                : 'Elija la plantilla de certificado en el desplegable o seleccione en el historial una evolución de certificado (CMO/CMR).'
                                        }
                                        onClick={abrirModalSeleccionMedicinaLaboral}
                                    >
                                        <i className="fas fa-file-signature"></i> Generar Certificados
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        <div className="row">
                            {/* 🆕 Columna principal (9 columnas) */}
                            <div className="col-md-9">
                            {/* 1. Selector de Plantilla */}
                            <div className="card mb-3">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">
                                        <i className="fas fa-file-medical"></i> Seleccionar Plantilla
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="form-group mb-0">
                                        <label><strong>Plantilla:</strong></label>
                                        <select 
                                            className="form-control" 
                                            value={plantillaId}
                                            onChange={handlePlantillaChange}
                                            disabled={loadingPlantillas || edicionBorradorActiva}
                                        >
                                            <option value="">-- Seleccione una plantilla --</option>
                                            {plantillas.map(plantilla => (
                                                <option key={plantilla.id} value={plantilla.id}>
                                                    {plantilla.nombre} 
                                                    {plantilla.especialidad && ` (${plantilla.especialidad})`}
                                                </option>
                                            ))}
                                        </select>
                                        {loadingPlantillas && (
                                            <small className="text-muted">
                                                <i className="fas fa-spinner fa-spin"></i> Cargando plantillas...
                                            </small>
                                        )}
                                        {!loadingPlantillas && plantillas.length === 0 && (
                                            <small className="text-warning">
                                                <i className="fas fa-exclamation-triangle"></i> No tiene plantillas asignadas. 
                                                Contacte al administrador.
                                            </small>
                                        )}
                                        {!loadingPlantillas && plantillas.length > 0 && !plantillaId && (
                                            <small className="text-info d-block mt-2">
                                                <i className="fas fa-info-circle"></i> Recuerde seleccionar la plantilla de historia clínica
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* 2. Fecha y Hora */}
                            <div className="card mb-3">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">
                                        <i className="fas fa-clock"></i> Fecha y Hora
                                    </h5>
                                </div>
                                <div className="card-body">
                                    {/* Profesional Tratante */}
                                    <div className="form-group">
                                        <label><i className="fas fa-user-md"></i> Profesional Tratante</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={nombreProfesional}
                                            readOnly
                                            style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}
                                        />
                                        <small className="text-muted">Usuario que realiza la historia clínica</small>
                                    </div>
                                    
                                    <div className="form-group mb-0">
                                        <label><strong>Fecha y Hora:</strong></label>
                                        <div className="input-group">
                                            <input 
                                                type="datetime-local" 
                                                className="form-control" 
                                                value={fechaHora} 
                                                onChange={(e) => setFechaHora(e.target.value)}
                                                readOnly={edicionBorradorActiva}
                                                disabled={edicionBorradorActiva}
                                            />
                                            <div className="input-group-append">
                                                <button 
                                                    type="button" 
                                                    className="btn btn-primary" 
                                                    onClick={establecerFechaHoraActual}
                                                    disabled={edicionBorradorActiva}
                                                >
                                                    <i className="fas fa-clock"></i> Fecha y Hora Actual
                                                </button>
                                            </div>
                                        </div>
                                        {edicionBorradorActiva && (
                                            <small className="text-muted d-block mt-1">
                                                En borrador, la fecha y el profesional no se pueden modificar.
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            
                            {/* 3. Campos Dinámicos de la Plantilla */}
                            {plantillaSeleccionada && (
                                <form onSubmit={handleSubmit} id="formHistoriaJson">
                                    {plantillaSeleccionada.secciones && plantillaSeleccionada.secciones.map((seccion, seccionIndex) => (
                                        <div key={seccionIndex} className="card mb-3">
                                            <div className="card-header bg-light">
                                                <h5 className="mb-0">
                                                    <i className="fas fa-folder-open"></i> {seccion.titulo}
                                                </h5>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    {seccion.campos && seccion.campos.map((campo, campoIndex) => (
                                                        <div 
                                                            key={campoIndex} 
                                                            className={campo.tipo === 'textarea' ? 'col-md-12' : 'col-md-6'}
                                                        >
                                                            <FieldFactory
                                                                campo={campo}
                                                                valor={valores[campo.id]}
                                                                onChange={handleFieldChange}
                                                                valores={valores}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    
                                    <div className="card mb-3">
                                        <div id="profesional-atiende-hc-json">
                                        <div className="card-header bg-light">
                                            <h5 className="mb-0">
                                                    <i className="fas fa-user-md"></i> Profesional que atiende
                                            </h5>
                                        </div>
                                            <div className="card-body" style={{ padding: '0.7rem' }}>
                                                <div className="row">
                                                    <div className="col-md-4">
                                                        <div className="form-group">
                                                            <label htmlFor="nombreProfHcJson">Profesional que atiende</label>
                                                <br />
                                                            {nombreProfesional}
                                            </div>
                                        </div>
                                                    <div className="col-md-4">
                                                        <div className="form-group">
                                                            <label>Registro médico</label>
                                                            <br />
                                                            {registroMedico || 'N/A'}
                                    </div>
                                        </div>
                                                    <div className="col-md-4">
                                                        <div className="form-group">
                                                            <label>Código de habilitación</label>
                                                            <br />
                                                            {codigoHabilitacion || 'N/A'}
                                                    </div>
                                                </div>
                                                    </div>
                                                <div className="row">
                                                    {firmaimg && (
                                                        <div className="col-md-4">
                                                            <div className="form-group" style={{ marginBottom: '0.4rem' }}>
                                                                <label>Firma</label>
                                                                <br />
                                                                <img src={firmaimg} className="firma-profesional-hc" alt="" />
                                                </div>
                                                    </div>
                                                    )}
                                                    <div className={firmaimg ? 'col-md-8' : 'col-md-12'}>
                                                        <div className="form-group" style={{ marginBottom: '0.4rem' }}>
                                                            <label>Pie de Firma</label>
                                                            <br />
                                                            {textoPieFirma ? (
                                                                <div style={{ whiteSpace: 'pre-line', fontSize: '0.75rem', color: '#495057', lineHeight: '1.6' }}>
                                                                    {textoPieFirma}
                                                </div>
                                                            ) : (
                                                                <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                                                    {nombreProfesional}
                                                        <br />
                                                                    <small>RM: {registroMedico || 'N/A'} | Hab: {codigoHabilitacion || 'N/A'}</small>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            )}
                            
                            {/* Mensaje cuando se está cargando la plantilla */}
                            {!plantillaSeleccionada && plantillaId && (
                                <div className="alert alert-info">
                                    <i className="fas fa-spinner fa-spin"></i> Cargando estructura de la plantilla...
                                </div>
                            )}
                            
                            {/* 🆕 Botones de Acción del Formulario - Movidos al final */}
                            {plantillaSeleccionada && (
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-12 text-right">
                                                <button 
                                                    className="btn btn-secondary mr-2"
                                                    onClick={() => history.goBack()}
                                                    disabled={loading}
                                                >
                                                    <i className="fas fa-arrow-left"></i> Volver
                                                </button>
                                                <button 
                                                    className="btn btn-outline-secondary mr-2"
                                                    type="button"
                                                    onClick={(e) => persistirHistoria(ESTADO_HISTORIA_BORRADOR, e)}
                                                    disabled={loading || !plantillaSeleccionada}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <i className="fas fa-spinner fa-spin"></i> Guardando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-pencil-alt"></i> Borrador
                                                        </>
                                                    )}
                                                </button>
                                                <button 
                                                    className="btn btn-primary" 
                                                    type="submit" 
                                                    form="formHistoriaJson"
                                                    disabled={loading || !plantillaSeleccionada}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <i className="fas fa-spinner fa-spin"></i> Guardando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-save"></i> Guardar Historia Clínica
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                            {/* 🆕 Columna del historial (3 columnas) */}
                            <div className="col-md-3" style={{ borderLeft: '2px solid #dee2e6' }}>
                                <div className="timeline-evolucion timeline-historia-clinica">
                                    <div className="titulo-historial" style={{ backgroundColor: '#f8f9fa', padding: '10px', border: '1px solid #dee2e6', marginBottom: '15px' }}>
                                        <i className="fas fa-history"></i> Historial de Historias Clínicas
                                    </div>
                                
                                    {historial.map((item) => {
                                        const esBorradorItem = esHistoriaBorrador(item);
                                        const esSeleccionado = historiaSeleccionada?.id === item.id;

                                        return (
                                        <div 
                                            className={`timeline-item-container${esBorradorItem ? ' timeline-borrador' : ''}${esSeleccionado ? ' selected' : ''}`}
                                            key={item.id} 
                                            onClick={() => verHistoria(item)} 
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="timeline-punto-container">
                                                <div 
                                                    className="timeline-punto"
                                                    style={
                                                        esSeleccionado
                                                            ? {
                                                                boxShadow: esBorradorItem
                                                                    ? '0 0 0 4px rgba(255, 193, 7, 0.35)'
                                                                    : '0 0 0 4px rgba(23, 162, 184, 0.3)',
                                                            }
                                                            : {}
                                                    }
                                                ></div>
                                            </div>
                                            <div
                                                className="timeline-meta"
                                                title={item.plantilla_nombre || 'Historia clínica'}
                                            >
                                                <div className="timeline-meta-fecha">
                                                    <i className="fas fa-calendar-alt"></i>
                                                    <span>{fechaHistoriaSoloFechaCorta(item.fecha)}</span>
                                                    {registroTieneNotas(item.id) && (
                                                        <i
                                                            className="fas fa-sticky-note"
                                                            title="Tiene nota aclaratoria"
                                                            style={{ fontSize: '10px', color: '#17a2b8' }}
                                                        ></i>
                                                    )}
                                                    {puedeEditarBorrador(item) && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm p-0"
                                                            title="Continuar borrador"
                                                            onClick={(e) => continuarBorrador(item, e)}
                                                            style={{ lineHeight: 1, minWidth: 'auto' }}
                                                        >
                                                            <i className="fas fa-pencil-alt text-warning" style={{ fontSize: '10px' }}></i>
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="timeline-meta-plantilla">
                                                    {item.plantilla_nombre}
                                            </div>
                                        </div>
                                        </div>
                                        );
                                    })}
                                
                                    {historial.length === 0 && (
                                        <div className="text-center text-muted" style={{ padding: '20px' }}>
                                            <i className="fas fa-inbox"></i>
                                            <br />
                                            Sin registros
                                        </div>
                                    )}
                                    
                                    {/* 🆕 Botón Resumen Completo */}
                                    <div className="text-center mt-3">
                                        <button 
                                            className="btn btn-primary btn-sm btn-resumen-historico"
                                            onClick={() => {
                                                if (historialSoloGuardadas.length > 0) {
                                                    generarVistaConsolidada();
                                                } else {
                                                    alert('No hay historias guardadas para mostrar');
                                                }
                                            }}
                                            disabled={historialSoloGuardadas.length === 0}
                                        >
                                            <i className="fas fa-file-alt"></i> Resumen Completo
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <ScrollToEndsButton />
            <Footer />
            <ModalArchivosClinicos
                show={showArchivosClinicos}
                onHide={cerrarArchivosClinicos}
            />
            
            <Modal show={showModalElegirMedicinaLaboral} onHide={() => setShowModalElegirMedicinaLaboral(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fas fa-briefcase-medical"></i> Certificado Medicina Laboral
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2" style={{ fontSize: '14px' }}>
                        Seleccione Registro de Medicina Laboral. Para Generar el Certificado.
                    </p>
                    <label className="font-weight-bold">Registro de Medicina Laboral</label>
                    <select
                        className="form-control mt-1"
                        value={registroMedicinaLaboralElegido?.id ?? ''}
                        onChange={(e) => {
                            const id = Number(e.target.value);
                            const sel = historialesMedicinaLaboral.find((h) => Number(h.id) === id);
                            setRegistroMedicinaLaboralElegido(sel || null);
                        }}
                    >
                        {historialesMedicinaLaboral.map((h) => (
                            <option key={h.id} value={h.id}>
                                {fechaHistoriaParaDisplay(h.fecha)} — {h.plantilla_nombre || 'Medicina Laboral'}
                            </option>
                        ))}
                    </select>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModalElegirMedicinaLaboral(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            if (!registroMedicinaLaboralElegido) return;
                            setShowModalElegirMedicinaLaboral(false);
                            setShowModalVistaCertificadoLabor(true);
                        }}
                        disabled={!registroMedicinaLaboralElegido}
                    >
                        Generar con Registro Elegido
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showModalVistaCertificadoLabor}
                onHide={() => setShowModalVistaCertificadoLabor(false)}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Certificado médico — vista previa</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                    {historiaSeleccionada && (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: generarContenidoCertificadoConLaborHTML(
                                    historiaSeleccionada,
                                    registroMedicinaLaboralElegido
                                )
                            }}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={descargarPdfCertificadoLabor}>
                        <i className="fas fa-file-pdf"></i> Guardar PDF
                    </Button>
                    <Button variant="primary" onClick={imprimirCertificadoLabor}>
                        <i className="fas fa-print"></i> Imprimir
                    </Button>
                    <Button variant="secondary" onClick={() => setShowModalVistaCertificadoLabor(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* 🆕 Modal de Vista Previa */}
            <ModalNotaAclaratoria
                show={showModalNotaAclaratoria}
                onClose={() => setShowModalNotaAclaratoria(false)}
                tipo="historia"
                registroId={historiaSeleccionada?.id}
                profesionalNombre={historiaSeleccionada?.profesional || ''}
                notasRegistro={notasRegistroActual}
                puedeAgregar={puedeAgregarNotaAclaratoria}
                onSaved={async () => {
                    await cargarNotasAclaratorias();
                }}
            />

            <Modal show={showModalVistaPrevia} onHide={() => setShowModalVistaPrevia(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Historia Clínica</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {historiaSeleccionada && (
                        <div dangerouslySetInnerHTML={{ __html: generarContenidoHistoriaHTML(historiaSeleccionada) }} />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={descargarPdfHistoria}>
                        <i className="fas fa-file-pdf"></i> Guardar PDF
                    </Button>
                    <Button variant="primary" onClick={imprimirHistoria}>
                        <i className="fas fa-print"></i> Imprimir
                    </Button>
                    <Button
                        variant="success"
                        onClick={() => abrirModalEmailHistoria('individual')}
                        disabled={enviandoEmailHistoria}
                    >
                        {enviandoEmailHistoria && tipoEmailHistoria === 'individual' ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                Enviando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-envelope"></i> Email
                            </>
                        )}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowModalVistaPrevia(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* 🆕 Modal de Confirmación Post-Guardado */}
            <Modal show={showModalConfirmacion} onHide={handleCerrarModalConfirmacion} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fas fa-file-medical"></i> Historia Clínica Guardada
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: '16px', marginBottom: 0 }}>
                        {mensajeConfirmacion || 'La historia clínica ha sido guardada exitosamente.'}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCerrarModalConfirmacion}>
                        <i className="fas fa-times"></i> Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* Modal de Vista Consolidada */}
            <Modal show={showModalConsolidado} onHide={() => setShowModalConsolidado(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Historial Completo de Historias Clínicas</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                    <div dangerouslySetInnerHTML={{ __html: consolidado }} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={descargarPdfConsolidado} disabled={!historialSoloGuardadas.length}>
                        <i className="fas fa-file-pdf"></i> Guardar PDF
                    </Button>
                    <Button variant="primary" onClick={imprimirConsolidado} disabled={!historialSoloGuardadas.length}>
                        <i className="fas fa-print"></i> Imprimir
                    </Button>
                    <Button
                        variant="success"
                        onClick={() => abrirModalEmailHistoria('consolidado')}
                        disabled={!historialSoloGuardadas.length || enviandoEmailHistoria}
                    >
                        {enviandoEmailHistoria && tipoEmailHistoria === 'consolidado' ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                Enviando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-envelope"></i> Email
                            </>
                        )}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowModalConsolidado(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showModalEmailHistoria}
                onHide={() => !enviandoEmailHistoria && setShowModalEmailHistoria(false)}
                centered
                dialogClassName="mx-email-dialog"
                contentClassName="mx-email-content"
            >
                <Modal.Header closeButton className="mx-email-head">
                    <Modal.Title>
                        <span className="mx-email-head-icon">
                            <i className="fas fa-envelope"></i>
                        </span>
                        Enviar Historia Clínica por Email
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="mx-email-body">
                    <div className="mx-email-info">
                        <i className="fas fa-info-circle"></i>
                        <div className="mx-email-info-text">
                            <span className="mx-email-info-desc">
                                {tipoEmailHistoria === 'individual' ? (
                                    <>
                                        Se enviará la historia <strong>{historiaSeleccionada?.plantilla_nombre || 'clínica'}</strong> en PDF.
                                    </>
                                ) : (
                                    <>Se enviará el <strong>resumen completo</strong> de historias clínicas en PDF.</>
                                )}
                            </span>
                            {obtenerProfesionalesParaEmail(tipoEmailHistoria) && (
                                <span className="mx-email-info-prof">
                                    Profesional: {obtenerProfesionalesParaEmail(tipoEmailHistoria)}
                                </span>
                            )}
                        </div>
                    </div>
                    <label className="mx-email-label" htmlFor="emailDestinoHistoria">
                        Correo electrónico de destino <span className="text-danger">*</span>
                    </label>
                    <div className="mx-email-input-wrap">
                        <input
                            id="emailDestinoHistoria"
                            type="email"
                            className={`mx-email-input${emailDestinoHistoria.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailDestinoHistoria.trim()) ? ' is-invalid' : ''}`}
                            value={emailDestinoHistoria}
                            onChange={(e) => setEmailDestinoHistoria(e.target.value)}
                            placeholder="ejemplo@correo.com"
                            autoFocus
                            disabled={enviandoEmailHistoria}
                        />
                        <span className={`mx-email-input-icon${/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((emailDestinoHistoria || '').trim()) ? ' is-valid' : ''}`}>
                            {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((emailDestinoHistoria || '').trim())
                                ? <i className="fas fa-check-circle"></i>
                                : <i className="fas fa-envelope"></i>}
                        </span>
                    </div>
                    <small className="mx-email-help">
                        Por defecto se usa el correo del paciente; puede cambiarlo si es necesario.
                    </small>
                </Modal.Body>
                <Modal.Footer className="mx-email-footer">
                    <Button
                        className="mx-email-btn-cancel"
                        onClick={() => setShowModalEmailHistoria(false)}
                        disabled={enviandoEmailHistoria}
                    >
                        Cancelar
                    </Button>
                    <Button
                        className="mx-email-btn-send"
                        onClick={enviarHistoriaPorEmail}
                        disabled={enviandoEmailHistoria}
                    >
                        {enviandoEmailHistoria ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                Enviando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-paper-plane mr-1"></i> Enviar
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modales de RIPS */}
            <Modal show={showRipsSinFev} onHide={() => setShowRipsSinFev(false)} size="xl" enforceFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Registro RIPS Sin FEV</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <RegistroRipsSinFev paciente={obtenerPacienteParaRips()} />
                </Modal.Body>
            </Modal>

            <Modal show={showRipsFev} onHide={() => setShowRipsFev(false)} size="xl" enforceFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Registro RIPS FEV</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <RegistroRipsFev paciente={obtenerPacienteParaRips()} onClose={() => setShowRipsFev(false)} />
                </Modal.Body>
            </Modal>

            <Modal show={showRipsConvenio} onHide={cerrarRipsConvenio} size="xl" enforceFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Registro RIPS Por Convenio</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <RegistroRipsConvenio cargarHistorial={cargarHistorialRipsConvenio} cerrarModal={cerrarRipsConvenio} paciente={obtenerPacienteParaRips()} />
                </Modal.Body>
            </Modal>

            {/* Modales de Documentos */}
            <DocumentoPacienteModal show={showExamenes} onHide={cerrarExamenes} title="Ayudas Diagnósticas">
                <ModalExamenes cargarHistorial={cargarHistorialExamenes} cerrarModal={cerrarExamenes} anamnesis={getInfoAnamnesisPDF} docPaciente={paciente?.numeroDocumento || paciente?.numero_documento} paciente={paciente} />
            </DocumentoPacienteModal>

            <DocumentoPacienteModal show={showFormulas} onHide={cerrarFormulas} title="Fórmulas Médicas">
                <ModalFormulas cargarHistorial={cargarHistorialFormulas} cerrarModal={cerrarFormulas} anamnesis={getInfoAnamnesisPDF} docPaciente={paciente?.numeroDocumento || paciente?.numero_documento} paciente={paciente} />
            </DocumentoPacienteModal>

            <DocumentoPacienteModal show={showIncapacidades} onHide={cerrarIncapacidades} title="Incapacidades">
                <ModalIncapacidades cargarHistorial={cargarHistorialIncapacidades} cerrarModal={cerrarIncapacidades} anamnesis={getInfoAnamnesisPDF} docPaciente={paciente?.numeroDocumento || paciente?.numero_documento} paciente={paciente} />
            </DocumentoPacienteModal>

            <DocumentoPacienteModal show={showRecomendaciones} onHide={cerrarRecomendaciones} title="Recomendaciones">
                <ModalRecomendaciones cargarHistorial={cargarHistorialRecomendaciones} cerrarModal={cerrarRecomendaciones} anamnesis={getInfoAnamnesisPDF} docPaciente={paciente?.numeroDocumento || paciente?.numero_documento} paciente={paciente} />
            </DocumentoPacienteModal>

            <DocumentoPacienteModal show={showRemisiones} onHide={cerrarRemisiones} title="Remisiones">
                <ModalRemisiones cargarHistorial={cargarHistorialRemisiones} cerrarModal={cerrarRemisiones} anamnesis={getInfoAnamnesisPDF} docPaciente={paciente?.numeroDocumento || paciente?.numero_documento} paciente={paciente} />
            </DocumentoPacienteModal>

            <DocumentoPacienteModal show={showContraremisiones} onHide={cerrarContraremisiones} title="Contraremisiones">
                <ModalContraremisiones cargarHistorial={cargarHistorialContraremisiones} cerrarModal={cerrarContraremisiones} anamnesis={getInfoAnamnesisPDF} docPaciente={paciente?.numeroDocumento || paciente?.numero_documento} paciente={paciente} />
            </DocumentoPacienteModal>

            <DocumentoPacienteModal show={showConsentimientos} onHide={cerrarConsentimientos} title="Consentimientos">
                <ModalConsentimientos cargarHistorial={cargarHistorialConsentimientos} cerrarModal={cerrarConsentimientos} anamnesis={getInfoAnamnesisPDF} docPaciente={paciente?.numeroDocumento || paciente?.numero_documento} paciente={paciente} />
            </DocumentoPacienteModal>

            <ModalOdontograma
                show={showOdontograma}
                onHide={cerrarOdontograma}
                pacienteId={paciente_id}
                paciente={{
                    primerNombre: paciente?.primerNombre ?? paciente?.primer_nombre ?? '',
                    segundoNombre: paciente?.segundoNombre ?? paciente?.segundo_nombre ?? '',
                    primerApellido: paciente?.primerApellido ?? paciente?.primer_apellido ?? '',
                    segundoApellido: paciente?.segundoApellido ?? paciente?.segundo_apellido ?? '',
                    tipoDocumento: paciente?.tipoDocumento ?? paciente?.tipo_documento ?? '',
                    numeroDocumento: paciente?.numeroDocumento ?? paciente?.numero_documento ?? '',
                }}
            />
        </>
    );
};

export default HistoriaClinicaJsonPage;
